import React, { useEffect, useMemo, useState, useContext } from "react";
import { useFormik } from "formik";
import { get, post, put } from "../../helper/Api_helper";
import Table from "../../Common/Table/Table";
import invoiceHeaderIcon from "../../Assets/Images/invoice-outline.svg";
import invoiceHeaderDarkIcon from "../../Assets/Images/invoicepage-icon-dark.svg";
import {
  VYLABS_BASE_URL,
  GET_INVOICE_CANDIDATES,
  INVOICE_CREATE,
  INVOICE_PREVIEW,
  TAX_CONFIG_LIST,
  INVOICE_CALCULATE_BILLING,
  INVOICE_CALCULATE_MARGIN,
  GET_FINANCIAL_LIST,
  INVOICE_DOWNLOAD,
  INVOICE_UPDATE,
  INVOICE_GET_BY_ID,
  CHANGE_INVOICE_STATUS,
} from "../../helper/url_helper";
import Button from "../../Common/Button/Button";
import "./InvoiceList.css";
import * as Yup from "yup";
import InvoiceCalculationCard from "./InvoiceCalculationCard.jsx";
import { DynamicContext } from "../../Context/DynamicContext";
import SidePopup from "../../Common/SidePopup/SidePopup";
import DashboardCard from "../../Common/Card/DashboardCard";
import { CustomDatePickerModal } from "../../Common/Date-Picker/DatePicker";
import DynamicSelectInput from "../../Common/Select-Input/SelectInput";
import DynamicTextField from "../../Common/Textfield/Textfield";
import clientIcon from "../../Assets/Images/client-invoice-icon.svg";
import jobIcon from "../../Assets/Images/jobs-invoice-icon.svg";
import recruiterIcon from "../../Assets/Images/recruiter-invoice-icon.svg";
import VybogTALLoaderAnimation from "../../Common/Dynamic-Animations/DynamicAnimations";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import EmailPreviewPopup from "../../Common/EmailPreviewPopup/EmailPreviewPopup";
import { StandardPopup } from "../../Common/PopupStyles/PopupComponent";
import CustomAutocomplete from "../../Common/AutoComplete/AutoComplete";
import { filterPayload } from "../../Common/Common-Function/CommonFunctions";
import DynamicAutocomplete from "../../Common/AutoSuggestion/DynamicAutocomplete.js";
import {
  TENANT_PRIME_API_KEY,
} from "../../Tenant-Config/TenantConfig.js";
import CustomTooltip from "../../Common/Tooltip/Tooltip.js";
import infoIcon from "../../Assets/Images/infoicon.svg";
import DynamicCheckbox from "../../Common/Checkbox/Checkbox.js";
import ViewIcon from "../../Assets/Images/file-view-icon.svg";
import DownloadIcon from "../../Assets/Images/download-invoice-icon.png";
import editIcon from "../../Assets/Images/edit-icon.svg";
import { ConfirmationPopup } from "../../Common/PopupStyles/PopupComponent";
import { createPortal } from "react-dom";
import tickIcon from "../../Assets/Images/invoice-draft-tick.svg";
import cancelIcon from "../../Assets/Images/invoice-draft-cancel.svg";
import dotsIcon from "../../Assets/Images/invoice-draft-3dots.svg";
import Menuselector from "../../Common/Drop-Down-Menu/Menuselector.js";
import SimpleCepheaChat from "../../Common/Simple-Search-Box/SimpleSearchBox.js";
import DynamicListUsers from "../../Common/DynamicListUsers/DynamicListUsers.js";

const EMPTY_INVOICE_FORM_VALUES = {
  joiningDate: "",
  invoiceDate: "",
  country: "",
  hiringType: "",
  billingMode: "percentage",
  monthlyGrossSalary: "",
  annualBaseSalary: "",
  annualCtc: "",
  contractBillingMode: "PERIODIC",
  contractDurationWeeks: "",
  billRate: "",
  billableHours: "",
  payRate: "",
  servicePeriodStart: "",
  servicePeriodEnd: "",
  regionCode: "",
  parttimeHourlyRate: "",
  hoursPerWeek: "",
  parttimeEarningsWeeks: "",
  finalPlacementFeePercent: "",
  placementFee: "",
  paymentTerms: "",
  recruiterCost: "",
  jobBoardSourcingCost: "",
  backgroundCheckCost: "",
  otherOperationalCosts: "",
  referralIncluded: false,
  referrer: "",
  referrerType: "",
  referrerUser: null,
  rewardType: "",
  rewardPercent: "",
  financialProfile: null,
};

const BILLING_DETAIL_FIELD_NAMES = [
  "billingMode",
  "monthlyGrossSalary",
  "annualBaseSalary",
  "annualCtc",
  "contractBillingMode",
  "contractDurationWeeks",
  "billRate",
  "billableHours",
  "payRate",
  "servicePeriodStart",
  "servicePeriodEnd",
  "regionCode",
  "parttimeHourlyRate",
  "hoursPerWeek",
  "parttimeEarningsWeeks",
  "finalPlacementFeePercent",
  "placementFee",
  "paymentTerms",
];

const toFormNumber = (value) => {
  if (value === undefined || value === null || value === "") return "";
  return value;
};

const parseFormDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date;
};

const toPayloadNumber = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === "string" && value.trim() === "") return null;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const addPayloadNumber = (payload, key, value) => {
  const numericValue = toPayloadNumber(value);
  if (numericValue !== null) {
    payload[key] = numericValue;
  }
};

const mapInvoiceToFormValues = (invoice, candidate) => {
  if (!invoice) {
    return {
      ...EMPTY_INVOICE_FORM_VALUES,
      country: candidate?.country || "",
      hiringType: candidate?.hiring_type || "",
    };
  }

  const feeMode = invoice.fee_mode || "percentage";
  const feeValue =
    feeMode === "flat"
      ? (invoice.flat_amount ?? invoice.placement_fee)
      : invoice.fee_percentage;

  return {
    ...EMPTY_INVOICE_FORM_VALUES,
    joiningDate: parseFormDate(invoice.date_of_joining),
    invoiceDate: parseFormDate(invoice.invoice_date),
    country: invoice.country || candidate?.country || "",
    hiringType: invoice.hiring_type || candidate?.hiring_type || "",
    billingMode: feeMode,
    monthlyGrossSalary: toFormNumber(invoice.monthly_salary) || null,
    annualBaseSalary: toFormNumber(invoice.annual_base_salary) || null,
    annualCtc: toFormNumber(invoice.annual_ctc) || null,
    contractBillingMode:
      invoice.contract_billing_mode ||
      EMPTY_INVOICE_FORM_VALUES.contractBillingMode,
    contractDurationWeeks:
      toFormNumber(invoice.contract_duration_weeks) || null,
    billRate: toFormNumber(invoice.bill_rate) || null,
    billableHours: toFormNumber(invoice.billable_hours) || null,
    payRate: toFormNumber(invoice.pay_rate) || null,
    servicePeriodStart: parseFormDate(invoice.service_period_start) || null,
    servicePeriodEnd: parseFormDate(invoice.service_period_end) || null,
    regionCode: invoice.region_code || "",
    parttimeHourlyRate: toFormNumber(invoice.parttime_hourly_rate) || null,
    hoursPerWeek: toFormNumber(invoice.hours_per_week) || null,
    parttimeEarningsWeeks:
      toFormNumber(invoice.parttime_earnings_weeks) || null,
    finalPlacementFeePercent: toFormNumber(feeValue) || null,
    placementFee: toFormNumber(invoice.placement_fee) || null,
    paymentTerms: invoice.payment_terms_days ?? "",
    recruiterCost: toFormNumber(invoice.cost_recruiter) || null,
    jobBoardSourcingCost: toFormNumber(invoice.cost_sourcing) || null,
    backgroundCheckCost: toFormNumber(invoice.cost_bgcheck) || null,
    otherOperationalCosts: toFormNumber(invoice.cost_other) || null,
    referralIncluded: Boolean(invoice.has_referral) || null,
    referrer: invoice.referrer_name || "",
    referrerType: invoice.referrer_type || "",
    referrerUser: invoice.referrer_id
      ? {
          id: invoice.referrer_id,
          value: invoice.referrer_id,
          label: invoice.referrer_name || "",
        }
      : null,
    rewardType: invoice.referral_reward_type || "",
    rewardPercent: toFormNumber(invoice.referral_reward_value) || null,
    financialProfile: invoice.financial_profile_id
      ? {
          id: invoice.financial_profile_id,
          value: invoice.financial_profile_id,
        }
      : null,
  };
};

function InvoiceList() {
  const { isDarkMode } = useContext(DynamicContext);
  const [invoice, setInvoice] = useState([]);
  const [invoiceTablePage, setInvoiceTablePage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isInvoiceDetailPopupOpen, setIsInvoiceDetailPopupOpen] =
    useState(false);
  const [selectedInvoiceCandidate, setSelectedInvoiceCandidate] =
    useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [isInvoiceDetailLoading, setIsInvoiceDetailLoading] = useState(false);
  const [activeInvoiceDatePicker, setActiveInvoiceDatePicker] = useState(null);
  const [activeInvoiceDatePickerSource, setActiveInvoiceDatePickerSource] =
    useState("invoice");
  const [invoiceDatePickerPosition, setInvoiceDatePickerPosition] = useState({
    top: null,
    left: null,
  });
  const [activeInvoiceStatus, setActiveInvoiceStatus] =
    useState("ready_to_invoice");
  const [invoicePreviewHtml, setInvoicePreviewHtml] = useState("");
  const [isInvoicePreviewOpen, setIsInvoicePreviewOpen] = useState(false);
  const [taxValue, setTaxValue] = useState([]);
  const [taxSearch, setTaxSearch] = useState("");
  const [selectedTaxConfigs, setSelectedTaxConfigs] = useState([]);
  const [billingCalculation, setBillingCalculation] = useState(null);
  const [isBillingCalculationPopupOpen, setIsBillingCalculationPopupOpen] =
    useState(false);
  const [isBillingCalculationLoading, setIsBillingCalculationLoading] =
    useState(false);
  const [popupBillingValues, setPopupBillingValues] = useState(
    EMPTY_INVOICE_FORM_VALUES,
  );
  const [popupSelectedTaxConfigs, setPopupSelectedTaxConfigs] = useState([]);
  const [popupBillingCalculation, setPopupBillingCalculation] = useState(null);
  const [isMarginCalculationPopupOpen, setIsMarginCalculationPopupOpen] =
    useState(false);
  const [isMarginCalculationLoading, setIsMarginCalculationLoading] =
    useState(false);
  const [marginCalculation, setMarginCalculation] = useState(null);
  const [popupMarginValues, setPopupMarginValues] = useState(
    EMPTY_INVOICE_FORM_VALUES,
  );
  const [popupMarginCalculation, setPopupMarginCalculation] = useState(null);
  const [financeValue, setFinanceValue] = useState([]);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [isInvoicePreviewLoading, setIsInvoicePreviewLoading] = useState(false);
  const [invoiceMetrics, setInvoiceMetrics] = useState({
    ready_to_invoice: 0,
    drafts: 0,
    outstanding: 0,
    paid: 0,
    cancelled: 0,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [storeInvoice, setStoreInvoice] = useState(null);
  const [editFinancialProfile, setEditFinancialProfile] = useState(null);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [isPaidConfirmOpen, setIsPaidConfirmOpen] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isPublishingInvoice, setIsPublishingInvoice] = useState(false);

  const invoiceStatusTabs = [
    {
      label: "Ready to Invoice",
      value: "ready_to_invoice",
      title: "Candidates Ready for Invoice",
      subtitle:
        "Offer accepted or joined placements with no invoice raised yet.",
    },
    { label: "Drafts", value: "drafts", title: "Draft Invoices" },
    {
      label: "Outstanding",
      value: "outstanding",
      title: "Outstanding Invoices",
    },
    { label: "Paid", value: "paid", title: "Paid Invoices" },
    { label: "Cancelled", value: "cancelled", title: "Cancelled Invoices" },
  ];

  const countryOptions = [
    { title: "India", value: "INDIA" },
    { title: "United States", value: "USA" },
    { title: "Canada", value: "CANADA" },
  ];

  const hiringTypeOptions = [
    { title: "Contract", value: "contract" },
    { title: "Full time", value: "fulltime" },
    { title: "Part Time", value: "parttime" },
  ];

  const feeModeOptions = [
    { title: "Percentage", value: "percentage" },
    { title: "Fixed Fee", value: "flat" },
  ];

  const contractBillingModeOptions = [
    { title: "Periodic", value: "PERIODIC" },
    { title: "Whole Contract", value: "WHOLE_CONTRACT" },
  ];

  const paymentTermsOptions = [
    { title: "15 days", value: 15 },
    { title: "30 days", value: 30 },
    { title: "45 days", value: 45 },
    { title: "60 days", value: 60 },
    { title: "90 days", value: 90 },
  ];

  const referrerTypeOptions = [
    { title: "Internal employee", value: "INTERNAL_EMPLOYEE" },
    { title: "External referrer", value: "EXTERNAL_PARTNER" },
  ];

  const rewardTypeOptions = [
    { title: "Percentage of revenue", value: "PERCENTAGE" },
    { title: "Fixed amount", value: "FLAT" },
  ];

  const popupItems = [
    { name: "Mark as Paid", icon: tickIcon },
    { name: "Cancel Invoice", icon: cancelIcon },
  ];

  const INVOICE_CANDIDATES_PAGE_LIMIT = 20;

  const fetchInvoiceCandidates = async (searchData) => {
    try {
      setIsLoading(true);
      let allCandidates = [];
      let offset = 0;
      let hasMore = true;
      let latestMetrics = null;

      while (hasMore) {
        const searchParam = searchData
          ? `&search=${encodeURIComponent(searchData)}`
          : "";
        const response = await get(
          VYLABS_BASE_URL,
          `${GET_INVOICE_CANDIDATES}?offset=${offset}&limit=${INVOICE_CANDIDATES_PAGE_LIMIT}${searchParam}`,
        );

        const candidates = Array.isArray(response?.candidates)
          ? response.candidates
          : [];
        allCandidates = allCandidates.concat(candidates);

        if (response?.metrics) {
          latestMetrics = response.metrics;
        }

        hasMore = Boolean(response?.has_more);
        offset += INVOICE_CANDIDATES_PAGE_LIMIT;
      }

      setInvoice(allCandidates);
      if (latestMetrics) {
        setInvoiceMetrics({
          ready_to_invoice: latestMetrics.ready_to_invoice || 0,
          drafts: latestMetrics.drafts || 0,
          outstanding: latestMetrics.outstanding || 0,
          paid: latestMetrics.paid || 0,
          cancelled: latestMetrics.cancelled || 0,
        });
      }
      return allCandidates;
    } catch (error) {
      console.error("fetchInvoiceCandidates", error);
      setInvoice([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceCandidates();
  }, []);

  const handleViewInvoiceCandidate = async (row, isEdit = false) => {
    if (!row?.candidate_id) return;

    try {
      setIsEditing(Boolean(isEdit));
      setStoreInvoice(null);
      setEditFinancialProfile(null);
      setSelectedInvoiceCandidate(row);
      setInvoiceDetail(null);
      setSelectedTaxConfigs([]);
      setTaxSearch("");
      setTaxValue([]);
      setBillingCalculation(null);
      setPopupMarginCalculation(null);
      setMarginCalculation(null);
      setIsMarginCalculationPopupOpen(false);
      setIsInvoiceDetailPopupOpen(true);
      setIsInvoiceDetailLoading(true);

      const params = new URLSearchParams();
      if (row?.invoice_status) {
        params.set("status", row?.invoice_status);
      }

      const response = await get(
        VYLABS_BASE_URL,
        `${GET_INVOICE_CANDIDATES}?candidate_id=${encodeURIComponent(
          row.candidate_id,
        )}&${params}`,
      );

      const detail = Array.isArray(response?.candidates)
        ? response.candidates[0]
        : response?.candidate || response;

      setInvoiceDetail(detail || row);
      if (isEdit) {
        await Promise.all([
          getInvoiceByID(detail?.invoice_id || row?.invoice_id),
          getFinancialList(""),
          getConfigList(""),
        ]);
      }
    } catch (error) {
      console.error("handleViewInvoiceCandidate", error);
      setInvoiceDetail(row);
    } finally {
      setIsInvoiceDetailLoading(false);
    }
  };

  const handleInvoicePreview = async (row) => {
    if (!row?.invoice_id) return;

    try {
      const response = await post(VYLABS_BASE_URL, INVOICE_PREVIEW, {
        id: encodeURIComponent(row.invoice_id),
      });

      setInvoicePreviewHtml(response);
      setIsInvoicePreviewOpen(true);
    } catch (error) {
      console.error("handleInvoicePreview", error);
    }
  };

  const downloadInvoice = async (row) => {
    if (!row?.invoice_id) return;

    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${INVOICE_DOWNLOAD}?id=${row.invoice_id}`,
      );

      const fileUrl = response?.url;
      if (!fileUrl) return;

      const fileResponse = await fetch(fileUrl);
      const blob = await fileResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      const candidateName = row.candidate_name || "unknown";
      const invoiceStatus = row.invoice_status || "unknown";
      const fileName = `${candidateName}_${invoiceStatus.toLowerCase()}_invoice.pdf`;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.log("download Invoice", error);
    }
  };

  const openOutstandingStatusConfirm = (row, type) => {
    if (!row?.invoice_id) return;
    setStoreInvoice(null);
    setInvoiceDetail(null);
    setSelectedInvoiceCandidate(row);
    if (type === "paid") {
      setIsPaidConfirmOpen(true);
    } else if (type === "cancel") {
      setIsCancelConfirmOpen(true);
    }
  };

  const handleOutstandingMenuSelect = (item, row) => {
    switch (item) {
      case "Mark as Paid":
        openOutstandingStatusConfirm(row, "paid");
        break;
      case "Cancel Invoice":
        openOutstandingStatusConfirm(row, "cancel");
        break;
      default:
        break;
    }
  };

  const changeInvoiceStatus = async (status) => {
    const invoiceId =
      storeInvoice?.id ||
      invoiceDetail?.invoice_id ||
      selectedInvoiceCandidate?.invoice_id;
    if (!invoiceId) {
      Toast("error", "Unable to find invoice to update.");
      return;
    }
    const payload = {
      status,
    };
    try {
      setIsPublishingInvoice(true);
      await post(
        VYLABS_BASE_URL,
        `${CHANGE_INVOICE_STATUS}?id=${invoiceId}`,
        payload,
      );
      Toast("success", `Invoice marked as ${status} successfully!`);
      setIsPublishConfirmOpen(false);
      setIsPaidConfirmOpen(false);
      setIsCancelConfirmOpen(false);
      handleCloseInvoiceDetailPopup();
      fetchInvoiceCandidates();
    } catch (error) {
      console.error("error in changeInvoiceStatus", error);
      Toast(
        "error",
        error.response?.data || "Failed to update invoice status.",
      );
    } finally {
      setIsPublishingInvoice(false);
    }
  };

  const handleCloseInvoiceDetailPopup = () => {
    setIsInvoiceDetailPopupOpen(false);
    setTimeout(() => {
      setIsEditing(false);
      setStoreInvoice(null);
      setEditFinancialProfile(null);
      setSelectedInvoiceCandidate(null);
      setInvoiceDetail(null);
      setSelectedTaxConfigs([]);
      setTaxSearch("");
      setTaxValue([]);
      setBillingCalculation(null);
      setPopupMarginCalculation(null);
      setIsMarginCalculationPopupOpen(false);
      setFinanceValue([]);
      setFinancialLoading(false);
      setActiveInvoiceDatePicker(null);
      setInvoiceDatePickerPosition({
        top: null,
        left: null,
      });
      invoiceFormik.resetForm({
        values: EMPTY_INVOICE_FORM_VALUES,
      });
    }, 500);
  };

  const formatRecruiterName = (row) => {
    return (
      `${row?.recruiter_first_name || ""} ${row?.recruiter_last_name || ""}`.trim() ||
      row?.recruiter_name ||
      "--"
    );
  };

  const getInvoiceDetailValue = (key) => {
    return invoiceDetail?.[key] || selectedInvoiceCandidate?.[key] || "--";
  };

  const formatInvoiceDate = (value) => {
    if (!value) return "--";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateOnly = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getDateValue = (value) => {
    if (!value) return new Date();

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? new Date() : date;
  };

  const handleOpenInvoiceDatePicker = (
    fieldName,
    event,
    source = "invoice",
  ) => {
    const fieldElement =
      event.currentTarget.closest(".invoice-form-field") || event.currentTarget;
    const rect = fieldElement.getBoundingClientRect();
    setActiveInvoiceDatePicker(fieldName);
    setActiveInvoiceDatePickerSource(source);
    setInvoiceDatePickerPosition({
      top: rect.bottom + 8,
      left: rect.left,
    });
  };

  const handleCloseInvoiceDatePicker = () => {
    setActiveInvoiceDatePicker(null);
    setActiveInvoiceDatePickerSource("invoice");
    setInvoiceDatePickerPosition({ top: null, left: null });
  };

  const requiredMixed = (msg) =>
    Yup.mixed()
      .required(msg)
      .test("not-empty", msg, (value) => {
        if (value === undefined || value === null) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        return true;
      });

  const getInvoiceValidationSchema = (values) => {
    const shape = {
      joiningDate: requiredMixed("Joining Date is required"),
      invoiceDate: requiredMixed("Invoice date is required"),
      country: Yup.string().required("Country is required"),
      hiringType: Yup.string().required("Hiring Type is required"),
      paymentTerms: requiredMixed("Payment terms (days) is required"),
      financialProfile: Yup.object()
        .nullable()
        .test(
          "financial-profile-required",
          "Financial Profile is required",
          (value) => Boolean(value?.id),
        ),
    };

    const country = values?.country;
    const hiringType = values?.hiringType;

    const isIndia = country === "INDIA";
    const isUSACanada = ["USA", "CANADA"].includes(country);
    const isUSACanadaContract = isUSACanada && hiringType === "contract";
    const isIndiaContractOrPartTime =
      isIndia && ["contract", "parttime"].includes(hiringType);
    const isUSACanadaPartTime = isUSACanada && hiringType === "parttime";
    const isPercentageBilling = values?.billingMode === "percentage";

    if (!isUSACanadaContract) {
      shape.finalPlacementFeePercent = requiredMixed(
        "Placement Fee is required",
      );
    }

    if (isUSACanadaContract) {
      shape.contractBillingMode = requiredMixed(
        "Contract Billing Mode is required",
      );
    } else {
      shape.billingMode = requiredMixed("Billing Mode is required");
    }

    if (hiringType === "fulltime") {
      if (isUSACanada && isPercentageBilling) {
        shape.annualBaseSalary = requiredMixed(
          "Annual Base Salary is required",
        );
      } else if (isPercentageBilling) {
        shape.monthlyGrossSalary = requiredMixed("Monthly Salary is required");
      }
    }

    if (hiringType === "contract" && !isIndia) {
      shape.billRate = requiredMixed("Bill Rate is required");
      shape.payRate = requiredMixed("Pay Rate is required");
      shape.servicePeriodStart = requiredMixed(
        "Service Period Start is required",
      );
      shape.servicePeriodEnd = requiredMixed("Service Period End is required");

      if (values?.contractBillingMode === "WHOLE_CONTRACT") {
        shape.contractDurationWeeks = requiredMixed(
          "Contract Duration (Weeks) is required",
        );
        shape.hoursPerWeek = requiredMixed("Hours per Week is required");
      } else {
        shape.billableHours = requiredMixed(
          "Billable Hours (Total) is required",
        );
      }
    }

    if (isIndiaContractOrPartTime) {
      if (isPercentageBilling) {
        shape.monthlyGrossSalary = requiredMixed("Monthly Salary is required");
      }

      if (hiringType === "contract" || isPercentageBilling) {
        shape.servicePeriodStart = requiredMixed(
          "Service Period Start is required",
        );
        shape.servicePeriodEnd = requiredMixed(
          "Service Period End is required",
        );
      }
    }

    if (
      hiringType === "parttime" &&
      !isIndiaContractOrPartTime &&
      isPercentageBilling
    ) {
      shape.parttimeHourlyRate = requiredMixed(
        "Part-time Hourly Rate is required",
      );
      shape.hoursPerWeek = requiredMixed("Hours per Week is required");
      shape.parttimeEarningsWeeks = requiredMixed(
        "Earning Period (Weeks) is required",
      );

      if (!isUSACanadaPartTime) {
        shape.payRate = requiredMixed("Pay Rate is required");
      }
    }

    return Yup.object(shape);
  };

  const invoiceFormik = useFormik({
    enableReinitialize: true,
    initialValues: (() => {
      const mapped = mapInvoiceToFormValues(
        storeInvoice,
        invoiceDetail || selectedInvoiceCandidate,
      );
      return {
        ...mapped,
        financialProfile: editFinancialProfile || mapped.financialProfile,
      };
    })(),
    onSubmit: (values) => {
      console.log("Invoice details", values);
    },
  });

  const isIndia = invoiceFormik.values.country === "INDIA";

  const isUSACanadaContract =
    ["USA", "CANADA"].includes(invoiceFormik.values.country) &&
    invoiceFormik.values.hiringType === "contract";

  const renderInvoiceFieldError = (fieldName) =>
    invoiceFormik.touched[fieldName] && invoiceFormik.errors[fieldName] ? (
      <div
        style={{
          color: "#d32f2f",
          fontSize: "12px",
          marginTop: "4px",
          lineHeight: "1.3",
        }}
      >
        {invoiceFormik.errors[fieldName]}
      </div>
    ) : null;

  const getSelectedTaxConfigIds = () =>
    selectedTaxConfigs
      .map((taxConfig) =>
        typeof taxConfig === "string"
          ? taxValue.find((option) => option.title === taxConfig)?.id
          : taxConfig?.id,
      )
      .filter(Boolean);
  const isInvoicePreviewReady = useMemo(() => {
    const values = invoiceFormik.values;
    const applicationId =
      invoiceDetail?.application_id || selectedInvoiceCandidate?.application_id;
    const feeAmount = values.finalPlacementFeePercent;
    const taxConfigIds = getSelectedTaxConfigIds();

    const requiredValues = [
      applicationId,
      values.country,
      values.hiringType,
      values.billingMode,
      ...(isIndia ? [values.monthlyGrossSalary] : []),
      ...(!isUSACanadaContract ? [feeAmount] : []),
      values.financialProfile?.id,
    ];

    const hasAllRequiredValues = requiredValues.every(
      (value) =>
        value !== undefined && value !== null && String(value).trim() !== "",
    );

    return hasAllRequiredValues && taxConfigIds.length > 0;
  }, [
    invoiceFormik.values,
    selectedTaxConfigs,
    taxValue,
    invoiceDetail,
    selectedInvoiceCandidate,
  ]);

  const handlePreviewBeforeCreate = async () => {
    if (!isInvoicePreviewReady || isInvoicePreviewLoading) return;

    const values = invoiceFormik.values;
    const applicationId =
      invoiceDetail?.application_id || selectedInvoiceCandidate?.application_id;
    const taxConfigIds = getSelectedTaxConfigIds();
    const payload = {
      application_id: applicationId,
      country: values.country,
      hiring_type: values.hiringType,
      ...(shouldSendFeeMode(values) ? { fee_mode: values.billingMode } : {}),
      ...(shouldSendContractBillingMode(values)
        ? { contract_billing_mode: values.contractBillingMode }
        : {}),
      financial_profile_id: values.financialProfile?.id,
      tax_config_ids: taxConfigIds,
      ...getBillingPayloadFields(values),
      ...getFeePayload(values),
      date_of_joining: values.joiningDate
        ? formatDateOnly(values.joiningDate)
        : undefined,
      invoice_date: values.invoiceDate
        ? formatDateOnly(values.invoiceDate)
        : undefined,
      payment_terms_days: toPayloadNumber(values.paymentTerms),
      cost_recruiter: toPayloadNumber(values.recruiterCost),
      cost_sourcing: toPayloadNumber(values.jobBoardSourcingCost),
      cost_bgcheck: toPayloadNumber(values.backgroundCheckCost),
      cost_other: toPayloadNumber(values.otherOperationalCosts),
      has_referral: values.referralIncluded,
      referrer_name: values.referrer || undefined,
      referrer_type: values.referrerType || undefined,
      referral_reward_type: values.rewardType || undefined,
      referral_reward_value: toPayloadNumber(values.rewardPercent),
    };

    try {
      setIsInvoicePreviewLoading(true);
      const response = await post(
        VYLABS_BASE_URL,
        INVOICE_PREVIEW,
        filterPayload(payload),
      );

      setInvoicePreviewHtml(response);
      setIsInvoicePreviewOpen(true);
    } catch (error) {
      console.error("handlePreviewBeforeCreate", error);
      Toast("error", error.response?.data || "Failed to generate preview.");
    } finally {
      setIsInvoicePreviewLoading(false);
    }
  };

  const normalizeInvoiceStatus = (row) => {
    const status = String(row.invoice_status || row.status || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    if (["draft", "drafts"].includes(status)) return "drafts";
    if (["cancelled", "canceled"].includes(status)) return "cancelled";
    if (["ready", "ready_to_invoice", "joined", ""].includes(status)) {
      return "ready_to_invoice";
    }
    return status;
  };

  const invoiceStatusCounts = invoiceMetrics;

  const selectedInvoiceStatusTab =
    invoiceStatusTabs.find((tab) => tab.value === activeInvoiceStatus) ||
    invoiceStatusTabs[0];

  const filteredInvoice = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return invoice.filter((row) => {
      const matchesStatus = normalizeInvoiceStatus(row) === activeInvoiceStatus;
      if (!matchesStatus) return false;
      if (!normalizedSearch) return true;

      return [
        row.candidate_name,
        row.candidate_email,
        row.candidate_phone,
        row.client_name,
        row.recruiter_name,
        row.job_title,
        row.invoice_number,
        row.application_id,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        );
    });
  }, [activeInvoiceStatus, invoice, searchQuery]);

  const handleSearchSubmit = async () => {
    setSearchQuery(searchInput);
    setInvoiceTablePage(1);
    const candidates = await fetchInvoiceCandidates(searchInput);
    const status = candidates?.[0]?.invoice_status;
    if (status) {
      setActiveInvoiceStatus(
        status === "DRAFT" ? "drafts" : status.toLowerCase(),
      );
    }
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearchQuery("");
    fetchInvoiceCandidates();
    setActiveInvoiceStatus("ready_to_invoice");
    setInvoiceTablePage(1);
  };

  const handleStatusChange = (status) => {
    setActiveInvoiceStatus(status);
    setInvoiceTablePage(1);
  };

  const invoiceSummaryCards = [
    {
      title: "Client",
      value: getInvoiceDetailValue("client_name"),
      headerIcon: clientIcon,
      iconBgClass: "invoice-summary-icon",
    },
    {
      title: "Job",
      value: getInvoiceDetailValue("job_title"),
      headerIcon: jobIcon,
      iconBgClass: "invoice-summary-icon",
    },
    {
      title: "Recruiter",
      value: formatRecruiterName(invoiceDetail || selectedInvoiceCandidate),
      headerIcon: recruiterIcon,
      iconBgClass: "invoice-summary-icon",
    },
  ];

  const invoiceColumns = [
    {
      name: "Candidate",
      selector: (row) => row.candidate_name || "--",
      sortable: true,
      minWidth: "220px",
      cell: (row) => (
        <div className="invoice-candidate-cell">
          <span className="invoice-primary-text">
            {row.candidate_name || "--"}
          </span>
          <span className="invoice-secondary-text">
            {row.candidate_email || "--"}
          </span>
          <span className="invoice-secondary-text">
            {row.candidate_phone || "--"}
          </span>
        </div>
      ),
    },
    {
      name: "Job",
      selector: (row) => row.job_title || "--",
      sortable: true,
      minWidth: "220px",
    },
    {
      name: "Client",
      selector: (row) => row.client_name || "--",
      sortable: true,
      minWidth: "160px",
    },
    {
      name: "Recruiter",
      selector: formatRecruiterName,
      sortable: true,
      minWidth: "170px",
    },
    {
      name: "Stage",
      selector: (row) => row.stage || "--",
      sortable: true,
      minWidth: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          {activeInvoiceStatus === "ready_to_invoice" ? (
            <Button
              label="Create"
              className="invoice-generate-button"
              backgroundColor="transparent"
              color="#6860ee"
              border="1px solid #6860ee"
              hoverStyles={{
                backgroundColor: "#6860ee",
                color: "#ffffff",
                transform: "none",
              }}
              disabled={isInvoiceDetailLoading}
              event={() => handleViewInvoiceCandidate(row)}
            />
          ) : null}
          {activeInvoiceStatus === "drafts" ? (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "80px",
              }}
            >
              <CustomTooltip title={"Preview Invioce"} placement="bottom">
                <img
                  src={ViewIcon}
                  alt="ViewIcon"
                  style={{
                    cursor: "pointer",
                    width: "20px",
                  }}
                  onClick={() => handleInvoicePreview(row)}
                />
              </CustomTooltip>
              <CustomTooltip title={"Edit Invioce"} placement="bottom">
                <img
                  src={editIcon}
                  alt="editIcon"
                  style={{
                    cursor: "pointer",
                    width: "20px",
                  }}
                  onClick={() => {
                    handleViewInvoiceCandidate(row, true);
                  }}
                />
              </CustomTooltip>
            </div>
          ) : null}
          {activeInvoiceStatus === "outstanding" ? (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "80px",
              }}
            >
              <CustomTooltip title={"Preview Invioce"} placement="bottom">
                <img
                  src={ViewIcon}
                  alt="ViewIcon"
                  style={{
                    cursor: "pointer",
                    width: "20px",
                  }}
                  onClick={() => handleInvoicePreview(row)}
                />
              </CustomTooltip>
              <CustomTooltip title={"Download Invioce"} placement="bottom">
                <img
                  src={DownloadIcon}
                  alt="DownloadIcon"
                  style={{
                    cursor: "pointer",
                    width: "20px",
                  }}
                  onClick={() => downloadInvoice(row)}
                />
              </CustomTooltip>
              <Menuselector
                options={popupItems}
                onSelect={(item) => handleOutstandingMenuSelect(item, row)}
                menuStyle={{
                  "& .MuiPaper-root": {
                    width: "190px",
                  },
                }}
              />
            </div>
          ) : null}
          {activeInvoiceStatus === "paid" ? (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "80px",
              }}
            >
              <CustomTooltip title={"Preview Invioce"} placement="bottom">
                <img
                  src={ViewIcon}
                  alt="ViewIcon"
                  style={{
                    cursor: "pointer",
                    width: "20px",
                  }}
                  onClick={() => handleInvoicePreview(row)}
                />
              </CustomTooltip>
              <CustomTooltip title={"Download Invioce"} placement="bottom">
                <img
                  src={DownloadIcon}
                  alt="DownloadIcon"
                  style={{
                    cursor: "pointer",
                    width: "20px",
                  }}
                  onClick={() => downloadInvoice(row)}
                />
              </CustomTooltip>
            </div>
          ) : null}
          {activeInvoiceStatus === "cancelled" ? (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "80px",
              }}
            >
              <CustomTooltip title={"Preview Invioce"} placement="bottom">
                <img
                  src={ViewIcon}
                  alt="ViewIcon"
                  style={{
                    cursor: "pointer",
                    width: "20px",
                  }}
                  onClick={() => handleInvoicePreview(row)}
                />
              </CustomTooltip>
            </div>
          ) : null}
        </>
      ),
      width: "100px",
      center: "true",
      ignoreRowClick: true,
    },
  ];

  const normalizeInvoiceCountry = (country) =>
    String(country || "")
      .trim()
      .toUpperCase();

  const isUSACanadaCountry = (country) =>
    ["USA", "UNITED STATES", "CANADA"].includes(
      normalizeInvoiceCountry(country),
    );

  const isUSACanadaContractPayload = (values) =>
    isUSACanadaCountry(values.country) && values.hiringType === "contract";

  const getBillingPayloadFields = (values) => {
    const payload = {};
    const country = normalizeInvoiceCountry(values.country);
    const isIndia = country === "INDIA";
    const isUSACanada = isUSACanadaCountry(country);
    const isPercentageBilling = values.billingMode === "percentage";
    const isIndiaContractOrPartTime =
      isIndia && ["contract", "parttime"].includes(values.hiringType);

    if (isIndiaContractOrPartTime) {
      if (isPercentageBilling) {
        addPayloadNumber(payload, "monthly_salary", values.monthlyGrossSalary);
      }
      if (values.regionCode !== "") {
        payload.region_code = values.regionCode;
      }
      if (
        values.servicePeriodStart &&
        (values.hiringType === "contract" || isPercentageBilling)
      ) {
        payload.service_period_start = formatDateOnly(
          values.servicePeriodStart,
        );
      }
      if (
        values.servicePeriodEnd &&
        (values.hiringType === "contract" || isPercentageBilling)
      ) {
        payload.service_period_end = formatDateOnly(values.servicePeriodEnd);
      }
      return payload;
    }

    if (values.hiringType === "fulltime") {
      if (!isPercentageBilling) {
        return payload;
      }

      if (isUSACanada) {
        addPayloadNumber(
          payload,
          "annual_base_salary",
          values.annualBaseSalary,
        );
      } else {
        addPayloadNumber(payload, "monthly_salary", values.monthlyGrossSalary);
      }
    }

    if (values.hiringType === "contract") {
      addPayloadNumber(payload, "bill_rate", values.billRate);
      if (
        shouldSendContractBillingMode(values) &&
        values.contractBillingMode !== ""
      ) {
        payload.contract_billing_mode = values.contractBillingMode;
      }
      if (values.contractBillingMode === "WHOLE_CONTRACT") {
        addPayloadNumber(
          payload,
          "contract_duration_weeks",
          values.contractDurationWeeks,
        );
        addPayloadNumber(payload, "hours_per_week", values.hoursPerWeek);
      } else if (shouldSendContractBillingMode(values)) {
        addPayloadNumber(payload, "billable_hours", values.billableHours);
      } else {
        addPayloadNumber(
          payload,
          "contract_duration_weeks",
          values.contractDurationWeeks,
        );
        addPayloadNumber(payload, "hours_per_week", values.hoursPerWeek);
        addPayloadNumber(payload, "billable_hours", values.billableHours);
      }
      addPayloadNumber(payload, "pay_rate", values.payRate);
      if (values.regionCode !== "") {
        payload.region_code = values.regionCode;
      }
      if (values.servicePeriodStart) {
        payload.service_period_start = formatDateOnly(
          values.servicePeriodStart,
        );
      }
      if (values.servicePeriodEnd) {
        payload.service_period_end = formatDateOnly(values.servicePeriodEnd);
      }
    }

    if (values.hiringType === "parttime") {
      if (!isPercentageBilling) {
        return payload;
      }

      addPayloadNumber(payload, "hours_per_week", values.hoursPerWeek);
      addPayloadNumber(
        payload,
        "parttime_earnings_weeks",
        values.parttimeEarningsWeeks,
      );
      addPayloadNumber(
        payload,
        "parttime_hourly_rate",
        values.parttimeHourlyRate,
      );
      const isUSACanadaPartTime =
        isUSACanada && values.hiringType === "parttime";

      if (!isUSACanadaPartTime) {
        addPayloadNumber(payload, "pay_rate", values.payRate);
      }
    }
    return payload;
  };

  const getFeePayload = (values) => {
    if (isUSACanadaContractPayload(values)) {
      return {};
    }

    if (values.billingMode === "flat") {
      return {
        flat_amount: toPayloadNumber(values.finalPlacementFeePercent),
      };
    }
    return {
      fee_percentage: toPayloadNumber(values.finalPlacementFeePercent),
    };
  };

  function shouldSendContractBillingMode(values) {
    return isUSACanadaContractPayload(values);
  }

  function shouldSendFeeMode(values) {
    return !isUSACanadaContractPayload(values);
  }

  const getPlacementFeeLabel = (values) => {
    if (values.billingMode === "percentage") {
      return "Placement Fee (%)";
    }

    const country = String(values.country || "")
      .trim()
      .toUpperCase();
    const currencySymbol = ["USA", "UNITED STATES", "CANADA"].includes(country)
      ? "$"
      : "₹";

    return `Placement Fee (${currencySymbol})`;
  };

  const resetBillingModeFields = (values, name, value) => {
    const nextValues = {
      ...values,
      [name]: value,
    };

    if (name === "billingMode" && values.billingMode !== value) {
      nextValues.finalPlacementFeePercent = "";

      if (value === "flat") {
        if (["USA", "CANADA"].includes(values.country)) {
          if (values.hiringType === "fulltime") {
            nextValues.annualBaseSalary = "";
          }

          if (values.hiringType === "parttime") {
            nextValues.parttimeHourlyRate = "";
            nextValues.hoursPerWeek = "";
            nextValues.parttimeEarningsWeeks = "";
          }
        }

        if (values.country === "INDIA") {
          if (
            ["fulltime", "parttime", "contract"].includes(values.hiringType)
          ) {
            nextValues.monthlyGrossSalary = "";
          }

          if (values.hiringType === "parttime") {
            nextValues.servicePeriodStart = "";
            nextValues.servicePeriodEnd = "";
          }
        }
      }
    }

    if (
      name === "contractBillingMode" &&
      values.contractBillingMode !== value
    ) {
      nextValues.contractDurationWeeks = "";
      nextValues.hoursPerWeek = "";
      nextValues.billableHours = "";
    }

    return nextValues;
  };

  const resetBillingDetailsFields = (values, overrides = {}) => {
    const nextValues = {
      ...values,
      ...overrides,
    };

    BILLING_DETAIL_FIELD_NAMES.forEach((fieldName) => {
      nextValues[fieldName] = EMPTY_INVOICE_FORM_VALUES[fieldName];
    });

    return nextValues;
  };

  const getFinancialProfileCountry = (profile) => {
    const country = normalizeInvoiceCountry(profile?.country);

    if (country === "UNITED STATES") return "USA";
    if (["INDIA", "USA", "CANADA"].includes(country)) return country;

    return profile?.country || "";
  };

  const clearBillingDetailsTouchedAndErrors = () => {
    const billingFields = new Set(BILLING_DETAIL_FIELD_NAMES);
    const removeBillingFields = (fields = {}) =>
      Object.keys(fields).reduce((nextFields, fieldName) => {
        if (!billingFields.has(fieldName)) {
          nextFields[fieldName] = fields[fieldName];
        }
        return nextFields;
      }, {});

    invoiceFormik.setTouched(removeBillingFields(invoiceFormik.touched), false);
    invoiceFormik.setErrors(removeBillingFields(invoiceFormik.errors));
  };

  const handleFinancialProfileChange = (value) => {
    const nextCountry = getFinancialProfileCountry(value);
    const isCountryChanged = invoiceFormik.values.country !== nextCountry;

    setBillingCalculation(null);

    if (isCountryChanged) {
      setSelectedTaxConfigs([]);
      invoiceFormik.setValues(
        resetBillingDetailsFields(invoiceFormik.values, {
          financialProfile: value,
          country: nextCountry,
          annualBaseSalary: "",
          monthlyGrossSalary: "",
        }),
      );
      clearBillingDetailsTouchedAndErrors();
      return;
    }

    invoiceFormik.setFieldValue("financialProfile", value);
  };

  const getBillingTaxConfigIds = (taxConfigs) =>
    taxConfigs
      .map((taxConfig) =>
        typeof taxConfig === "string"
          ? taxValue.find((option) => option.title === taxConfig)?.id
          : taxConfig?.id,
      )
      .filter(Boolean);

  const calculateBillingForValues = async (
    values,
    taxConfigs,
    onCalculated,
  ) => {
    const taxConfigIds = getBillingTaxConfigIds(taxConfigs);

    const payload = {
      country: values.country || "",
      ...(shouldSendFeeMode(values)
        ? { fee_mode: values.billingMode || "" }
        : {}),
      hiring_type: values.hiringType || "",
      ...getFeePayload(values),
      ...getBillingPayloadFields(values),
      tax_config_ids: taxConfigIds,
    };

    try {
      const response = await post(
        VYLABS_BASE_URL,
        INVOICE_CALCULATE_BILLING,
        filterPayload(payload),
      );
      onCalculated(response);
      Toast("success", "Billing Calculated Successfully.");
    } catch (error) {
      console.error("error in calculateBilling", error);
      Toast("error", error.response?.data || "Calculation Failed.");
    }
  };

  const calculateBilling = async () => {
    await calculateBillingForValues(
      invoiceFormik.values,
      selectedTaxConfigs,
      setBillingCalculation,
    );
  };

  const handleCalculateBillingInPopup = async () => {
    setIsBillingCalculationLoading(true);
    await calculateBillingForValues(
      popupBillingValues,
      popupSelectedTaxConfigs,
      setPopupBillingCalculation,
    );
    setIsBillingCalculationLoading(false);
  };

  const handlePopupBillingChange = (event) => {
    const { name, value } = event.target;
    setPopupBillingCalculation(null);
    setPopupBillingValues((previousValues) => {
      const nextValues = resetBillingModeFields(previousValues, name, value);

      return {
        ...nextValues,
        ...(["country", "hiringType"].includes(name) &&
        ["USA", "CANADA"].includes(
          name === "country" ? value : previousValues.country,
        ) &&
        (name === "hiringType" ? value : previousValues.hiringType) ===
          "contract"
          ? {
              contractBillingMode:
                previousValues.contractBillingMode ||
                EMPTY_INVOICE_FORM_VALUES.contractBillingMode,
            }
          : {}),
      };
    });
  };

  const handlePopupTaxConfigsChange = (taxConfigs) => {
    setPopupBillingCalculation(null);
    setPopupSelectedTaxConfigs(taxConfigs);
  };

  const getReferralPayload = (values) => {
    if (!values.referralIncluded) {
      return {
        has_referral: false,
        referrer_id: "",
        referrer_name: "",
        referrer_type: "",
        referral_reward_type: "",
        referral_reward_value: null,
      };
    }

    const isInternal = values.referrerType === "INTERNAL_EMPLOYEE";

    return {
      has_referral: true,
      referrer_id: isInternal ? values.referrerUser?.id || "" : "",
      referrer_name: isInternal ? "" : values.referrer || "",
      referrer_type: values.referrerType || "",
      referral_reward_type: values.rewardType || "",
      referral_reward_value: toPayloadNumber(values.rewardPercent),
    };
  };

  const calculateMarginForValues = async (values, onCalculated) => {
    const payload = {
      cost_bgcheck: toPayloadNumber(values.backgroundCheckCost),
      cost_contractor: null,
      cost_other: toPayloadNumber(values.otherOperationalCosts),
      cost_recruiter: toPayloadNumber(values.recruiterCost),
      cost_sourcing: toPayloadNumber(values.jobBoardSourcingCost),
      placement_fee: toPayloadNumber(values.placementFee),
      ...getReferralPayload(values),
    };

    try {
      const response = await post(
        VYLABS_BASE_URL,
        INVOICE_CALCULATE_MARGIN,
        filterPayload(payload),
      );

      onCalculated(response);
      Toast("success", "Margin Calculated Successfully.");
    } catch (error) {
      console.error("error in calculateMargin", error);
      Toast("error", error.response?.data || "Margin Calculation Failed.");
    }
  };

  const calculateMargin = async () => {
    setIsMarginCalculationLoading(true);
    await calculateMarginForValues(invoiceFormik.values, setMarginCalculation);
    setIsMarginCalculationLoading(false);
  };

  const renderMainMarginCalculationButton = () => (
    <Button
      label="Calculate"
      event={calculateMargin}
      disabled={isMarginCalculationLoading}
      className="invoice-calculate-button"
    />
  );

  const handleMainMarginChange = (event) => {
    const { name, value } = event.target;
    setMarginCalculation(null);

    if (name === "rewardType") {
      invoiceFormik.setFieldValue("rewardType", value);
      invoiceFormik.setFieldValue("rewardPercent", "");
      return;
    }

    if (name === "rewardPercent") {
      const numericValue = Number(value);
      if (
        value !== "" &&
        (numericValue < 0 ||
          (invoiceFormik.values.rewardType === "PERCENTAGE" &&
            (numericValue < 1 || numericValue > 100)))
      ) {
        return;
      }
    }

    invoiceFormik.handleChange(event);
  };

  const setMainMarginFieldValue = (fieldName, value) => {
    setMarginCalculation(null);
    invoiceFormik.setFieldValue(fieldName, value);
  };

  const handleCalculateMarginInPopup = async () => {
    setIsMarginCalculationLoading(true);
    await calculateMarginForValues(
      popupMarginValues,
      setPopupMarginCalculation,
    );
    setIsMarginCalculationLoading(false);
  };

  const handlePopupMarginChange = (event) => {
    const { name, value } = event.target;
    setPopupMarginCalculation(null);
    setPopupMarginValues((previousValues) => ({
      ...previousValues,
      [name]: value,
    }));
  };

  const setPopupMarginFieldValue = (name, value) => {
    setPopupMarginCalculation(null);
    setPopupMarginValues((previousValues) => ({
      ...previousValues,
      [name]: value,
    }));
  };

  const createInvoice = async () => {
    const values = invoiceFormik.values;

    const taxConfigIds = selectedTaxConfigs
      .map((taxConfig) =>
        typeof taxConfig === "string"
          ? taxValue.find((option) => option.title === taxConfig)?.id
          : taxConfig?.id,
      )
      .filter(Boolean);

    const shouldIncludeContractBillingMode =
      shouldSendContractBillingMode(values);

    const validationSchema = getInvoiceValidationSchema(values);
    let validationErrors = {};

    try {
      await validationSchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      if (validationError?.inner?.length) {
        validationErrors = validationError.inner.reduce((errors, error) => {
          if (error.path && !errors[error.path]) {
            errors[error.path] = error.message;
          }
          return errors;
        }, {});
      } else if (validationError?.path) {
        validationErrors[validationError.path] = validationError.message;
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      invoiceFormik.setErrors(validationErrors);
      invoiceFormik.setTouched(
        Object.keys(validationErrors).reduce((fields, fieldName) => {
          fields[fieldName] = true;
          return fields;
        }, {}),
        false,
      );
      Toast("error", "Please fill all required fields");
      return;
    }

    const payload = {
      ...(isEditing
        ? {
            id:
              storeInvoice?.id ||
              invoiceDetail?.invoice_id ||
              selectedInvoiceCandidate?.invoice_id,
          }
        : {}),
      application_id:
        invoiceDetail?.application_id ||
        selectedInvoiceCandidate?.application_id,
      financial_profile_id: values.financialProfile.id,
      date_of_joining: formatDateOnly(values.joiningDate),
      invoice_date: formatDateOnly(values.invoiceDate),
      payment_terms_days: toPayloadNumber(values.paymentTerms),
      country: values.country,
      hiring_type: values.hiringType,
      ...(shouldSendFeeMode(values) ? { fee_mode: values.billingMode } : {}),
      ...(shouldIncludeContractBillingMode
        ? { contract_billing_mode: values.contractBillingMode }
        : {}),
      ...getFeePayload(values),
      tax_config_ids: taxConfigIds,
      cost_bgcheck: toPayloadNumber(values.backgroundCheckCost),
      cost_contractor: null,
      cost_other: toPayloadNumber(values.otherOperationalCosts),
      cost_recruiter: toPayloadNumber(values.recruiterCost),
      cost_sourcing: toPayloadNumber(values.jobBoardSourcingCost),
      ...getBillingPayloadFields(values),
      ...getReferralPayload(values),
    };
    try {
      const res = isEditing
        ? await put(
            VYLABS_BASE_URL,
            `${INVOICE_UPDATE}?id=${
              storeInvoice?.id ||
              invoiceDetail?.invoice_id ||
              selectedInvoiceCandidate?.invoice_id
            }`,
            filterPayload(payload),
          )
        : await post(VYLABS_BASE_URL, INVOICE_CREATE, filterPayload(payload));
      Toast(
        "success",
        `Invoice ${isEditing ? "Updated" : "Created"} Successfully!`,
      );
      handleCloseInvoiceDetailPopup();
      fetchInvoiceCandidates();
    } catch (error) {
      console.error("error in createInvoice", error);

      Toast(
        "error",
        error.response?.data ||
          `Failed to ${isEditing ? "edit" : "create"} invoice.`,
      );
    }
  };

  const getConfigList = async (search = "") => {
    try {
      const query = new URLSearchParams({
        limit: "50",
        offset: "0",
      });

      if (search.trim()) {
        query.append("search", search.trim());
      }

      const response = await get(
        VYLABS_BASE_URL,
        `${TAX_CONFIG_LIST}?${query.toString()}`,
      );

      const data = Array.isArray(response?.tax_configs)
        ? response.tax_configs
        : [];

      const formattedOptions = data.map((taxConfig) => ({
        id: taxConfig.id,
        title: `${taxConfig.country} - ${taxConfig.tax_type}`,
      }));

      setTaxValue(formattedOptions);
    } catch (error) {
      console.error("taxConfig", error);
      setTaxValue([]);
    }
  };

  useEffect(() => {
    if (!isInvoiceDetailPopupOpen) return;

    getConfigList(taxSearch);
  }, [taxSearch, isInvoiceDetailPopupOpen]);

  const getFinancialList = async (search = "") => {
    try {
      setFinancialLoading(true);

      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };

      const query = new URLSearchParams();

      if (search.trim()) {
        query.append("search", search.trim());
      }

      const url = query.toString()
        ? `${GET_FINANCIAL_LIST}?${query.toString()}`
        : GET_FINANCIAL_LIST;

      const response = await get(VYLABS_BASE_URL, url, requestConfig);

      const data = Array.isArray(response?.financial_profiles)
        ? response.financial_profiles
        : [];

      const formattedOptions = data.map((profile) => ({
        ...profile,
        value: profile.id,
        label: profile.profile_name,
      }));

      setFinanceValue(formattedOptions);
    } catch (error) {
      console.error("financial profile", error);
      setFinanceValue([]);
    } finally {
      setFinancialLoading(false);
    }
  };

  const selectedFinancialProfile = invoiceFormik.values.financialProfile;

  const getFinanceProfileValue = (key) => {
    return selectedFinancialProfile?.[key] || null;
  };

  const mainBillingFormik = invoiceFormik;
  const mainBillingTaxConfigs = selectedTaxConfigs;
  const handleMainBillingChange = (event) => {
    const { name, value } = event.target;
    setBillingCalculation(null);

    if (["billingMode", "contractBillingMode"].includes(name)) {
      invoiceFormik.setValues(
        resetBillingModeFields(invoiceFormik.values, name, value),
      );
      return;
    }

    invoiceFormik.handleChange(event);
  };
  const setMainBillingTaxConfigs = (taxConfigs) => {
    setBillingCalculation(null);
    setSelectedTaxConfigs(taxConfigs);
  };

  const renderBillingDetailsScenario = ({
    values = mainBillingFormik.values,
    handleChange = handleMainBillingChange,
    taxConfigs = mainBillingTaxConfigs,
    setTaxConfigs = setMainBillingTaxConfigs,
    calculateEvent = calculateBilling,
    calculationLoading = false,
    showBillingModeToggle = false,
    datePickerSource = "invoice",
    sectionClassName = "invoice-billing-section",
  } = {}) => {
    const invoiceFormik = { values, handleChange };
    const selectedTaxConfigs = taxConfigs;
    const setSelectedTaxConfigs = setTaxConfigs;
    const isIndia = invoiceFormik.values.country === "INDIA";
    const isContractOrPartTime = ["contract", "parttime"].includes(
      invoiceFormik.values.hiringType,
    );
    const isIndiaContractOrPartTime = isIndia && isContractOrPartTime;
    const isUSACanadaPartTime =
      ["USA", "CANADA"].includes(invoiceFormik.values.country) &&
      invoiceFormik.values.hiringType === "parttime";
    const isUSACanadaContract =
      ["USA", "CANADA"].includes(invoiceFormik.values.country) &&
      invoiceFormik.values.hiringType === "contract";
    const isPeriodicContract =
      isUSACanadaContract &&
      invoiceFormik.values.contractBillingMode === "PERIODIC";
    const isWholeContract =
      isUSACanadaContract &&
      invoiceFormik.values.contractBillingMode === "WHOLE_CONTRACT";
    const billingModeToggleField = isUSACanadaContract
      ? "contractBillingMode"
      : "billingMode";
    const billingModeToggleOptions = isUSACanadaContract
      ? contractBillingModeOptions
      : feeModeOptions;
    const shouldHidePopupAnnualBaseSalary =
      showBillingModeToggle &&
      ["USA", "CANADA"].includes(invoiceFormik.values.country) &&
      invoiceFormik.values.hiringType === "fulltime" &&
      invoiceFormik.values.billingMode === "flat";
    const shouldHidePopupIndiaFullTimeFixedFeeMonthlySalary =
      showBillingModeToggle &&
      invoiceFormik.values.country === "INDIA" &&
      invoiceFormik.values.hiringType === "fulltime" &&
      invoiceFormik.values.billingMode === "flat";
    const shouldHidePopupPartTimeFixedFeeInputs =
      showBillingModeToggle &&
      ["USA", "CANADA"].includes(invoiceFormik.values.country) &&
      invoiceFormik.values.hiringType === "parttime" &&
      invoiceFormik.values.billingMode === "flat";
    const shouldHidePopupIndiaPartTimePercentageServicePeriod =
      showBillingModeToggle &&
      invoiceFormik.values.country === "INDIA" &&
      invoiceFormik.values.hiringType === "parttime" &&
      invoiceFormik.values.billingMode === "percentage";
    const shouldHidePopupIndiaPartTimeFixedFeeFields =
      showBillingModeToggle &&
      invoiceFormik.values.country === "INDIA" &&
      invoiceFormik.values.hiringType === "parttime" &&
      invoiceFormik.values.billingMode === "flat";
    const shouldHidePopupIndiaContractFixedFeeMonthlySalary =
      showBillingModeToggle &&
      invoiceFormik.values.country === "INDIA" &&
      invoiceFormik.values.hiringType === "contract" &&
      invoiceFormik.values.billingMode === "flat";
    const renderInvoiceDatePickerButton = (fieldName) => (
      <Button
        type="button"
        className="invoice-date-picker-trigger calendarschedulerIcons"
        backgroundColor="var(--filter-button, #f8fafc)"
        color="var(--header__color)"
        border="1px solid var(--border__color, #dfe5f2)"
        width="100%"
        hoverd={false}
        style={{
          minHeight: "34px",
          padding: "8px 10px",
          borderRadius: "8px",
          justifyContent: "flex-start",
          textAlign: "left",
          fontSize: "14px",
          fontWeight: 500,
          boxShadow: "none",
        }}
        event={(event) =>
          handleOpenInvoiceDatePicker(fieldName, event, datePickerSource)
        }
      >
        {formatInvoiceDate(invoiceFormik.values[fieldName])}
      </Button>
    );

    return (
      <div className={sectionClassName}>
        <div className="invoice-billing-header">
          <div>
            <h3>Billing Details</h3>
            {invoiceFormik.values.country &&
              invoiceFormik.values.hiringType && (
                <p>
                  <strong>
                    {invoiceFormik.values.country} -{" "}
                    {invoiceFormik.values.hiringType}
                  </strong>{" "}
                  billing rules applied.
                </p>
              )}
          </div>
          {showBillingModeToggle && (
            <div
              className="invoice-billing-mode"
              role="radiogroup"
              aria-label={
                isUSACanadaContract ? "Contract billing mode" : "Fee mode"
              }
            >
              {billingModeToggleOptions.map((option) => {
                const isActive =
                  invoiceFormik.values[billingModeToggleField] === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`invoice-billing-mode__option ${
                      isActive ? "invoice-billing-mode__option--active" : ""
                    }`}
                    onClick={() =>
                      invoiceFormik.handleChange({
                        target: {
                          name: billingModeToggleField,
                          value: option.value,
                        },
                      })
                    }
                    role="radio"
                    aria-checked={isActive}
                  >
                    {option.title}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="invoice-billing-fields">
          {invoiceFormik.values.hiringType === "fulltime" && (
            <>
              {!shouldHidePopupAnnualBaseSalary &&
                !shouldHidePopupIndiaFullTimeFixedFeeMonthlySalary && (
                  <div>
                    <DynamicTextField
                      label={
                        ["USA", "CANADA"].includes(invoiceFormik.values.country)
                          ? "Annual Base Salary"
                          : "Monthly Salary"
                      }
                      name={
                        ["USA", "CANADA"].includes(invoiceFormik.values.country)
                          ? "annualBaseSalary"
                          : "monthlyGrossSalary"
                      }
                      value={
                        ["USA", "CANADA"].includes(invoiceFormik.values.country)
                          ? invoiceFormik.values.annualBaseSalary
                          : invoiceFormik.values.monthlyGrossSalary
                      }
                      onChange={invoiceFormik.handleChange}
                      placeholder={
                        ["USA", "CANADA"].includes(invoiceFormik.values.country)
                          ? "Enter annual base salary"
                          : "Enter monthly salary"
                      }
                      required
                      type="number"
                      containerStyle={{ width: "100%" }}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                      }}
                    />
                  </div>
                )}
              <div>
                <DynamicTextField
                  label={getPlacementFeeLabel(invoiceFormik.values)}
                  name="finalPlacementFeePercent"
                  value={invoiceFormik.values.finalPlacementFeePercent}
                  onChange={invoiceFormik.handleChange}
                  placeholder="Enter placement fee"
                  required
                  type="number"
                  containerStyle={{ width: "100%" }}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                />
              </div>
              <div>
                <DynamicSelectInput
                  label="Payment terms (days)"
                  name="paymentTerms"
                  options={paymentTermsOptions}
                  value={invoiceFormik.values.paymentTerms}
                  onChange={invoiceFormik.handleChange}
                  defaultOption="Select Terms"
                  menuWidth={220}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    display: "block",
                  }}
                  required
                />
              </div>
            </>
          )}

          {invoiceFormik.values.hiringType === "contract" &&
            !isIndiaContractOrPartTime && (
              <>
                {!isPeriodicContract && (
                  <div>
                    <DynamicTextField
                      label="Contract Duration (Weeks)"
                      name="contractDurationWeeks"
                      value={invoiceFormik.values.contractDurationWeeks}
                      onChange={invoiceFormik.handleChange}
                      placeholder="Enter duration"
                      type="number"
                      containerStyle={{ width: "100%" }}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                      }}
                      required
                    />
                  </div>
                )}
                {!isPeriodicContract && (
                  <div>
                    <DynamicTextField
                      label="Hours per Week"
                      name="hoursPerWeek"
                      value={invoiceFormik.values.hoursPerWeek}
                      onChange={invoiceFormik.handleChange}
                      placeholder="Enter hours"
                      type="number"
                      containerStyle={{ width: "100%" }}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                      }}
                      required
                    />
                  </div>
                )}
                <div>
                  <DynamicTextField
                    label="Bill Rate"
                    name="billRate"
                    value={invoiceFormik.values.billRate}
                    onChange={invoiceFormik.handleChange}
                    placeholder="Enter bill rate"
                    type="number"
                    containerStyle={{ width: "100%" }}
                    labelStyle={{
                      color: "var(--sidepopup-label-color, #64748b)",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginBottom: "6px",
                    }}
                    required
                  />
                </div>
                {!isWholeContract && (
                  <div>
                    <DynamicTextField
                      label="Billable Hours (Total)"
                      name="billableHours"
                      value={invoiceFormik.values.billableHours}
                      onChange={invoiceFormik.handleChange}
                      placeholder="Enter billable hours"
                      type="number"
                      containerStyle={{ width: "100%" }}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                      }}
                      required
                    />
                  </div>
                )}
                <div>
                  <DynamicTextField
                    label="Pay Rate"
                    name="payRate"
                    value={invoiceFormik.values.payRate}
                    onChange={invoiceFormik.handleChange}
                    placeholder="Enter pay rate"
                    type="number"
                    containerStyle={{ width: "100%" }}
                    labelStyle={{
                      color: "var(--sidepopup-label-color, #64748b)",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginBottom: "6px",
                    }}
                    required
                  />
                </div>
                {!isUSACanadaContract && (
                  <div>
                    <DynamicTextField
                      label={getPlacementFeeLabel(invoiceFormik.values)}
                      name="finalPlacementFeePercent"
                      value={invoiceFormik.values.finalPlacementFeePercent}
                      onChange={invoiceFormik.handleChange}
                      placeholder="Enter placement fee"
                      type="number"
                      containerStyle={{ width: "100%" }}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                      }}
                    />
                  </div>
                )}
                <div className="invoice-form-field">
                  <label htmlFor="popupServicePeriodStart">
                    Service Period Start
                    <span style={{ color: "red" }}> *</span>
                  </label>
                  {renderInvoiceDatePickerButton("servicePeriodStart")}
                </div>
                <div className="invoice-form-field">
                  <label htmlFor="popupServicePeriodEnd">
                    Service Period End
                    <span style={{ color: "red" }}> *</span>
                  </label>
                  {renderInvoiceDatePickerButton("servicePeriodEnd")}
                </div>
                <div>
                  <DynamicSelectInput
                    label="Payment terms (days)"
                    name="paymentTerms"
                    options={paymentTermsOptions}
                    value={invoiceFormik.values.paymentTerms}
                    onChange={invoiceFormik.handleChange}
                    defaultOption="Select Terms"
                    menuWidth={220}
                    labelStyle={{
                      color: "var(--sidepopup-label-color, #64748b)",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginBottom: "6px",
                      display: "block",
                    }}
                    required
                  />
                </div>
              </>
            )}

          {invoiceFormik.values.hiringType === "parttime" &&
            !isIndiaContractOrPartTime && (
              <>
                {!shouldHidePopupPartTimeFixedFeeInputs && (
                  <>
                    <div>
                      <DynamicTextField
                        label="Part-time Hourly Rate"
                        name="parttimeHourlyRate"
                        value={invoiceFormik.values.parttimeHourlyRate}
                        onChange={invoiceFormik.handleChange}
                        placeholder="Enter hourly rate"
                        type="number"
                        containerStyle={{ width: "100%" }}
                        labelStyle={{
                          color: "var(--sidepopup-label-color, #64748b)",
                          fontSize: "14px",
                          fontWeight: 600,
                          marginBottom: "6px",
                        }}
                        required
                      />
                    </div>
                    <div>
                      <DynamicTextField
                        label="Hours per Week"
                        name="hoursPerWeek"
                        value={invoiceFormik.values.hoursPerWeek}
                        onChange={invoiceFormik.handleChange}
                        placeholder="Enter hours"
                        type="number"
                        containerStyle={{ width: "100%" }}
                        labelStyle={{
                          color: "var(--sidepopup-label-color, #64748b)",
                          fontSize: "14px",
                          fontWeight: 600,
                          marginBottom: "6px",
                        }}
                        required
                      />
                    </div>
                    <div>
                      <DynamicTextField
                        label="Earning Period (Weeks)"
                        name="parttimeEarningsWeeks"
                        value={invoiceFormik.values.parttimeEarningsWeeks}
                        onChange={invoiceFormik.handleChange}
                        placeholder="Enter earning period"
                        type="number"
                        containerStyle={{ width: "100%" }}
                        labelStyle={{
                          color: "var(--sidepopup-label-color, #64748b)",
                          fontSize: "14px",
                          fontWeight: 600,
                          marginBottom: "6px",
                        }}
                        required
                      />
                    </div>
                  </>
                )}
                {!isUSACanadaPartTime && (
                  <div>
                    <DynamicTextField
                      label="Pay Rate"
                      name="payRate"
                      value={invoiceFormik.values.payRate}
                      onChange={invoiceFormik.handleChange}
                      placeholder="Enter pay rate"
                      type="number"
                      containerStyle={{ width: "100%" }}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                      }}
                      required
                    />
                  </div>
                )}
                <div>
                  <DynamicTextField
                    label={getPlacementFeeLabel(invoiceFormik.values)}
                    name="finalPlacementFeePercent"
                    value={invoiceFormik.values.finalPlacementFeePercent}
                    onChange={invoiceFormik.handleChange}
                    placeholder="Enter placement fee"
                    type="number"
                    containerStyle={{ width: "100%" }}
                    labelStyle={{
                      color: "var(--sidepopup-label-color, #64748b)",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginBottom: "6px",
                    }}
                    required
                  />
                </div>
                <div>
                  <DynamicSelectInput
                    label="Payment terms (days)"
                    name="paymentTerms"
                    options={paymentTermsOptions}
                    value={invoiceFormik.values.paymentTerms}
                    onChange={invoiceFormik.handleChange}
                    defaultOption="Select Terms"
                    menuWidth={220}
                    labelStyle={{
                      color: "var(--sidepopup-label-color, #64748b)",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginBottom: "6px",
                      display: "block",
                    }}
                    required
                  />
                </div>
              </>
            )}

          {isIndiaContractOrPartTime && (
            <>
              {!shouldHidePopupIndiaContractFixedFeeMonthlySalary &&
                !shouldHidePopupIndiaPartTimeFixedFeeFields && (
                  <div>
                    <DynamicTextField
                      label="Monthly Salary"
                      name="monthlyGrossSalary"
                      value={invoiceFormik.values.monthlyGrossSalary}
                      onChange={invoiceFormik.handleChange}
                      placeholder="Enter monthly salary"
                      type="number"
                      containerStyle={{ width: "100%" }}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                      }}
                      required
                    />
                  </div>
                )}
              <div>
                <DynamicTextField
                  label={getPlacementFeeLabel(invoiceFormik.values)}
                  name="finalPlacementFeePercent"
                  value={invoiceFormik.values.finalPlacementFeePercent}
                  onChange={invoiceFormik.handleChange}
                  placeholder="Enter placement fee"
                  type="number"
                  containerStyle={{ width: "100%" }}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                  required
                />
              </div>
              <div>
                <DynamicSelectInput
                  label="Payment terms (days)"
                  name="paymentTerms"
                  options={paymentTermsOptions}
                  value={invoiceFormik.values.paymentTerms}
                  onChange={invoiceFormik.handleChange}
                  defaultOption="Select Terms"
                  menuWidth={220}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    display: "block",
                  }}
                  required
                />
              </div>
              {!shouldHidePopupIndiaPartTimePercentageServicePeriod &&
                !shouldHidePopupIndiaPartTimeFixedFeeFields && (
                  <>
                    <div className="invoice-form-field">
                      <label htmlFor="popupIndiaServicePeriodStart">
                        Service Period Start
                        <span style={{ color: "red" }}> *</span>
                      </label>
                      {renderInvoiceDatePickerButton("servicePeriodStart")}
                    </div>
                    <div className="invoice-form-field">
                      <label htmlFor="popupIndiaServicePeriodEnd">
                        Service Period End
                        <span style={{ color: "red" }}> *</span>
                      </label>
                      {renderInvoiceDatePickerButton("servicePeriodEnd")}
                    </div>
                  </>
                )}
            </>
          )}
        </div>

        <div className="invoice-popup-tax-fields">
          <label>Taxes</label>
          <span>Select the taxes that apply to this invoice.</span>
          <CustomAutocomplete
            tags
            options={taxValue}
            value={selectedTaxConfigs}
            onChange={setSelectedTaxConfigs}
            onInputChange={setTaxSearch}
            placeholder="Search taxes"
            fieldLabel=""
            returnObjects
            serverSearch
            freeSolo={false}
            showClearAll
          />
        </div>

        <div className="invoice-popup-calculate-action">
          <Button
            label={calculationLoading ? "Calculating..." : "Calculate"}
            event={calculateEvent}
            disabled={calculationLoading}
          />
        </div>
      </div>
    );
  };

  const renderMarginProfitabilityScenario = ({
    values,
    handleChange,
    setFieldValue,
    calculation,
    calculateEvent,
    calculationLoading = false,
  }) => (
    <div className="invoice-margin-calculation-popup">
      <div className="invoice-profitability-referral-card invoice-profitability-referral-card--popup">
        <div className="invoice-profitability-card">
          <div className="invoice-profitability-heading">
            <div>
              <h3>Margin & Profitability</h3>
              <p>
                Internal only. These values never appear on the printable
                invoice.
              </p>
            </div>
          </div>
          <div className="invoice-profitability-grid">
            <div className="invoice-profitability-field">
              <DynamicTextField
                label="Recruiter cost (prorated)"
                name="recruiterCost"
                value={values.recruiterCost}
                onChange={handleChange}
                placeholder="0"
                type="text"
                containerStyle={{ width: "100%" }}
                labelStyle={{
                  color: "var(--sidepopup-label-color, #64748b)",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "6px",
                }}
              />
            </div>
            <div className="invoice-profitability-field">
              <DynamicTextField
                label="Job board / Sourcing cost"
                name="jobBoardSourcingCost"
                value={values.jobBoardSourcingCost}
                onChange={handleChange}
                placeholder="0"
                type="text"
                containerStyle={{ width: "100%" }}
                labelStyle={{
                  color: "var(--sidepopup-label-color, #64748b)",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "6px",
                }}
              />
            </div>
            <div className="invoice-profitability-field">
              <DynamicTextField
                label="Background check cost"
                name="backgroundCheckCost"
                value={values.backgroundCheckCost}
                onChange={handleChange}
                placeholder="0"
                type="text"
                containerStyle={{ width: "100%" }}
                labelStyle={{
                  color: "var(--sidepopup-label-color, #64748b)",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "6px",
                }}
              />
            </div>
            <div className="invoice-profitability-field">
              <DynamicTextField
                label="Other operational costs"
                name="otherOperationalCosts"
                value={values.otherOperationalCosts}
                onChange={handleChange}
                placeholder="0"
                type="text"
                containerStyle={{ width: "100%" }}
                labelStyle={{
                  color: "var(--sidepopup-label-color, #64748b)",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "6px",
                }}
              />
            </div>
            {!values.referralIncluded && (
              <div className="invoice-referral-field">
                <DynamicTextField
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      Placement fee
                      <CustomTooltip
                        title="Calculating the billing will obtain the placement fee."
                        arrow
                      >
                        <img
                          alt="infoIcon"
                          src={infoIcon}
                          style={{ width: "18px" }}
                        />
                      </CustomTooltip>
                    </span>
                  }
                  name="placementFee"
                  value={values.placementFee}
                  onChange={handleChange}
                  placeholder="Enter placement fee"
                  type="number"
                  containerStyle={{ width: "100%" }}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
            )}
          </div>
        </div>
        {/* <div className="invoice-referral-card">
          <div className="invoice-referral-header">
            <label className="invoice-referral-toggle">
              <DynamicCheckbox
                name="referralIncluded"
                checked={values.referralIncluded}
                onChange={(event) =>
                  setFieldValue("referralIncluded", event.target.checked)
                }
                isDarkMode={isDarkMode}
                color="var(--primary__color)"
              />
              <span className="invoice-referral-title">
                Placement includes referral
              </span>
            </label>

            <p>Attach a referral reward and deduct it live from net profit.</p>
          </div>

          {values.referralIncluded && (
            <div className="invoice-referral-grid">
              <div className="invoice-referral-field">
                <DynamicSelectInput
                  label="Referrer type"
                  name="referrerType"
                  options={referrerTypeOptions}
                  value={values.referrerType}
                  onChange={handleChange}
                  defaultOption="Select"
                  menuWidth={220}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    display: "block",
                  }}
                />
              </div>

              <div className="invoice-referral-field">
                {values.referrerType === "INTERNAL_EMPLOYEE" ? (
                  <div>
                    <label
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "var(--sidepopup-label-color, #64748b)",
                      }}
                    >
                      Referrer
                    </label>
                    <DynamicAutocomplete
                      options={filterUsers}
                      value={values.referrerUser}
                      onChange={(value) => {
                        setFieldValue("referrerUser", value);
                      }}
                      onOpen={() => {
                        if (!usersLoaded) {
                          fetchAllUsers();
                        }
                      }}
                      onInputChange={(event, value, reason) => {
                        if (reason !== "input") return;

                        const searchValue = value.trim().toLowerCase();

                        if (!searchValue) {
                          setFilterUsers(users);
                          return;
                        }

                        const filtered = users.filter((user) =>
                          [
                            user?.name,
                            user?.first_name,
                            user?.last_name,
                            user?.email,
                          ]
                            .filter(Boolean)
                            .some((field) =>
                              String(field).toLowerCase().includes(searchValue),
                            ),
                        );

                        setFilterUsers(filtered);
                      }}
                      placeholder="Search internal employee"
                      noOptionsText="No employees found"
                      customHeight="null"
                      sx={{
                        ".MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          height: "40px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid var(--border__color) !important",
                            borderRadius: "12px",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6860ee",
                            borderRadius: "12px",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#7a70f2",
                            borderRadius: "12px",
                          },
                        },
                        ".MuiInputBase-input": {
                          color: "var(--header__color)",
                          padding: "8px 12px !important",
                          height: "40px",
                          fontFamily: "Satoshi !important",
                        },
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            backgroundColor: "var(--body__background)",
                            color: "var(--header__color)",
                            borderRadius: "12px",
                            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                            border: "1px solid var(--border__color)",
                          },
                        },
                        listbox: {
                          sx: {
                            "& li": {
                              fontSize: "14px",
                              fontWeight: 500,
                              padding: "8px 16px",
                              color: "var(--header__color)",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                ) : (
                  <DynamicTextField
                    label="Referrer"
                    name="referrer"
                    value={values.referrer}
                    onChange={handleChange}
                    placeholder="Enter referrer name"
                    type="text"
                    containerStyle={{ width: "100%" }}
                    labelStyle={{
                      color: "var(--sidepopup-label-color, #64748b)",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginBottom: "6px",
                    }}
                  />
                )}
              </div>
              <div className="invoice-referral-field">
                <DynamicTextField
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      Placement fee
                      <CustomTooltip
                        title="Calculating the billing will obtain the placement fee."
                        arrow
                      >
                        <img
                          alt="infoIcon"
                          src={infoIcon}
                          style={{ width: "18px" }}
                        />
                      </CustomTooltip>
                    </span>
                  }
                  name="placementFee"
                  value={values.placementFee}
                  onChange={handleChange}
                  placeholder="Enter placement fee"
                  type="number"
                  containerStyle={{ width: "100%" }}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
              <div className="invoice-referral-field">
                <DynamicSelectInput
                  label="Reward type"
                  name="rewardType"
                  options={rewardTypeOptions}
                  value={values.rewardType}
                  onChange={handleChange}
                  defaultOption="Select"
                  menuWidth={220}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    display: "block",
                  }}
                />
              </div>
              <div className="invoice-referral-field">
                <DynamicTextField
                  label="Reward value"
                  name="rewardPercent"
                  value={values.rewardPercent}
                  onChange={handleChange}
                  placeholder="Enter reward value"
                  type="number"
                  containerStyle={{ width: "100%" }}
                  labelStyle={{
                    color: "var(--sidepopup-label-color, #64748b)",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
            </div>
          )}
        </div> */}
      </div>

      <div className="invoice-popup-calculate-action">
        <Button
          label={calculationLoading ? "Calculating..." : "Calculate"}
          event={calculateEvent}
          disabled={calculationLoading}
        />
      </div>

      {calculation && (
        <InvoiceCalculationCard marginCalculation={calculation} type="margin" />
      )}
    </div>
  );

  const getInvoiceByID = async (id) => {
    try {
      const res = await get(VYLABS_BASE_URL, `${INVOICE_GET_BY_ID}?id=${id}`);
      setStoreInvoice(res?.invoice);
    } catch (error) {
      console.error("error in getInvoiceByID", error);
    }
  };

  useEffect(() => {
    if (!isEditing || !storeInvoice) return;
    const financialProfileId = storeInvoice?.financial_profile_id;

    if (financialProfileId && financeValue.length > 0) {
      const matchedFinanceProfile = financeValue.find(
        (profile) => profile.id === financialProfileId,
      );

      if (matchedFinanceProfile) {
        invoiceFormik.setFieldValue("financialProfile", matchedFinanceProfile);
      }
    }
    const taxConfigIds = storeInvoice?.taxes || [];

    if (taxConfigIds.length > 0 && taxValue.length > 0) {
      const matchedTaxConfigs = taxValue.filter((tax) =>
        taxConfigIds.some((item) => item.tax_config_id === tax.id),
      );

      setSelectedTaxConfigs(matchedTaxConfigs);
    }
  }, [isEditing, storeInvoice, financeValue, taxValue]);

  return (
    <div>
      <div className="header-main-container">
        <div className="header-icon-title-container">
          <img
            alt=""
            className="header-title-icon"
            src={isDarkMode ? invoiceHeaderDarkIcon : invoiceHeaderIcon}
          />
          <h2 className="header-container">Invoice</h2>
        </div>

        {/* <div className="candidate-header-button-container">
          <div className="selection-actions">
            <div className="select-button-container">
              <Button
                label="Generate Invoice"
                iconSrc={addIcon}
                iconWidth="15px"
                className="create-button"
                style={{ padding: "5px 10px" }}
              />
            </div>
          </div>
        </div> */}
      </div>

      <div className="invoice-page-outer-container">
        <div className="invoice-search-container-wrap">
          <SimpleCepheaChat
            askMeAnythingPlaceholder="Search for candidate ( Name, P.No , Email )"
            askMeAnythingValue={searchInput}
            onAskChange={(e) => setSearchInput(e.target.value)}
            isHeaderNeeded={false}
            showCepheaActions={true}
            isDarkMode={isDarkMode}
            showExperienceAndLocation={false}
            showAttachResume={false}
            showJobDescriptionField={false}
            onSubmit={handleSearchSubmit}
            onClearSimpleSearch={handleSearchClear}
          />
        </div>

        <div className="invoice-status-filter" role="tablist">
          {invoiceStatusTabs.map((tab) => {
            const isActive = activeInvoiceStatus === tab.value;
            const count = invoiceStatusCounts[tab.value] || 0;

            return (
              <button
                key={tab.value}
                type="button"
                className={`invoice-status-filter__option ${
                  isActive ? "invoice-status-filter__option--active" : ""
                }`}
                onClick={() => handleStatusChange(tab.value)}
                role="tab"
                aria-selected={isActive}
              >
                <span>{tab.label}</span>
                <span className="invoice-status-filter__count">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="invoice-table-reslt">
          <div className="jobs-table-results">
            <div className="invoice-table-heading">
              <div>
                <h3>
                  {selectedInvoiceStatusTab.title}
                  <span>{filteredInvoice.length} invoices</span>
                </h3>
              </div>
            </div>
            <Table
              columns={invoiceColumns}
              data={filteredInvoice}
              currentPage={invoiceTablePage}
              onChangePage={setInvoiceTablePage}
              progressPending={isLoading}
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 250px)"
              className="global-data-table invoice-pipeline-table"
              backgroundColor="var(--table__background)"
              color="var(--header__color)"
              dataNotFoundMessage={`No ${selectedInvoiceStatusTab.label} invoice candidates found`}
            />
          </div>
        </div>
      </div>

      <SidePopup
        isOpen={isInvoiceDetailPopupOpen}
        onClose={handleCloseInvoiceDetailPopup}
        title={isEditing ? "Edit Invoice" : "Create Invoice"}
        sidePopupWidth="80%"
        sidePopupHeight="100%"
      >
        {isInvoiceDetailLoading ? (
          <div className="invoice-detail-loading">
            <VybogTALLoaderAnimation
              animationHeight="120px"
              animationWidth="120px"
              ariaLabel="Loading candidate details"
            />
            <span>Loading candidate details...</span>
          </div>
        ) : (
          <div className="invoice-create-container">
            <div className="invoice-create-summary">
              <div className="invoice-create-person">
                <div className="invoice-create-avatar">
                  {(getInvoiceDetailValue("candidate_name") || "--")
                    .charAt(0)
                    .toUpperCase()}
                </div>
                <div>
                  <h3>{getInvoiceDetailValue("candidate_name")}</h3>
                  <p>{getInvoiceDetailValue("job_title")}</p>
                </div>
              </div>

              {/* <div className="invoice-create-badges">
                <span className="invoice-create-badge invoice-create-badge--joined">
                  {getInvoiceDetailValue("stage")}
                </span>
                <span className="invoice-create-badge invoice-create-badge--draft">
                  Draft
                </span>
              </div> */}

              <div className="invoice-create-info-grid">
                {invoiceSummaryCards.map((card) => (
                  <DashboardCard
                    key={card.title}
                    title={card.title}
                    value={0}
                    trend={card.value}
                    headerIcon={card.headerIcon}
                    iconBgClass={card.iconBgClass}
                    animate={false}
                  />
                ))}
              </div>
            </div>

            <form
              className="invoice-placement-card"
              onSubmit={(event) => {
                event.preventDefault();
                createInvoice();
              }}
            >
              <div className="invoice-from-container">
                <div className="invoice-from-header">
                  <div>
                    <h3>Invoice From</h3>
                    <p>The company details that will appear on this invoice.</p>
                  </div>
                </div>

                <div className="invoice-financial-profile">
                  <label>
                    Select Financial Profile
                    <span style={{ color: "red" }}> *</span>
                  </label>
                  <DynamicAutocomplete
                    placeholder="Search financial profiles"
                    options={financeValue}
                    loading={financialLoading}
                    value={invoiceFormik.values.financialProfile}
                    onChange={handleFinancialProfileChange}
                    onOpen={() => {
                      getFinancialList("");
                    }}
                    onInputChange={(event, value, reason) => {
                      if (reason === "input") {
                        getFinancialList(value);
                      }
                    }}
                    noOptionsText="No financial profiles found"
                    sx={{
                      mt: "5px",
                      ".MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        height: "40px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid var(--border__color) !important",
                          borderRadius: "12px",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6860ee",
                          borderRadius: "12px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#7a70f2",
                          borderRadius: "12px",
                        },
                      },
                      ".MuiInputBase-input": {
                        color: "var(--header__color)",
                        padding: "8px 12px !important",
                        height: "40px",
                        fontFamily: "Satoshi !important",
                      },
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          backgroundColor: "var(--body__background)",
                          color: "var(--header__color)",
                          borderRadius: "12px",
                          boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                          border: "1px solid var(--border__color)",
                        },
                      },
                      listbox: {
                        sx: {
                          "& li": {
                            fontSize: "14px",
                            fontWeight: 500,
                            padding: "8px 16px",
                            color: "var(--header__color)",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          },
                        },
                      },
                    }}
                  />
                  {renderInvoiceFieldError("financialProfile")}
                </div>

                {selectedFinancialProfile ? (
                  <div className="invoice-from-grid">
                    <div className="invoice-from-card invoice-company-card">
                      <div className="invoice-from-card-title">
                        COMPANY INFORMATION
                      </div>

                      <div className="invoice-from-details">
                        <div>
                          <span>Legal Name</span>
                          <span>{getFinanceProfileValue("legal_name")}</span>
                        </div>

                        <div>
                          <span>Email</span>
                          <span>{getFinanceProfileValue("email")}</span>
                        </div>

                        <div>
                          <span>Phone</span>
                          <span>{getFinanceProfileValue("phone")}</span>
                        </div>

                        <div>
                          <span>Country</span>
                          <span>{getFinanceProfileValue("country")}</span>
                        </div>

                        <div>
                          <span>Address Line 1</span>
                          <span>{getFinanceProfileValue("address_line1")}</span>
                        </div>

                        <div>
                          <span>City</span>
                          <span>{getFinanceProfileValue("city")}</span>
                        </div>

                        <div>
                          <span>State</span>
                          <span>
                            {getFinanceProfileValue("state_or_province")}
                          </span>
                        </div>

                        <div>
                          <span>PIN Code</span>
                          <span>{getFinanceProfileValue("postal_code")}</span>
                        </div>
                      </div>
                    </div>
                    <div className="invoice-from-card invoice-tax-card">
                      <div className="invoice-from-card-title">
                        TAX REGISTRATION
                      </div>

                      <div className="invoice-from-details">
                        <div>
                          <span>Business Number</span>
                          <span>{getFinanceProfileValue("pan")}</span>
                        </div>

                        <div>
                          <span>GST/HST Number</span>
                          <span>{getFinanceProfileValue("gstin")}</span>
                        </div>
                      </div>
                    </div>
                    <div className="invoice-from-card invoice-payment-card">
                      <div className="invoice-from-card-title">
                        PAYMENT DETAILS
                      </div>

                      <div className="invoice-from-details">
                        <div>
                          <span>Bank Name</span>
                          <span>{getFinanceProfileValue("bank_name")}</span>
                        </div>

                        <div>
                          <span>Branch</span>
                          <span>{getFinanceProfileValue("bank_branch")}</span>
                        </div>

                        <div>
                          <span>Account Name</span>
                          <span>{getFinanceProfileValue("account_name")}</span>
                        </div>

                        <div>
                          <span>Account Number</span>
                          <span>
                            {getFinanceProfileValue("account_number")}
                          </span>
                        </div>

                        <div>
                          <span>Routing / Transit Number</span>
                          <span>{getFinanceProfileValue("ifsc_code")}</span>
                        </div>

                        <div>
                          <span>SWIFT / BIC</span>
                          <span>{getFinanceProfileValue("swift_bic")}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="invoice-from-grid-placeholder">
                    Select a financial profile above to view its company, tax
                    and payment details.
                  </div>
                )}
              </div>

              {/* <span className="invoice-placement-step">Step 1</span> */}
              {/* <h3>Placement Details</h3>
              <p>Candidate, job and client information linked to this invoice.</p> */}

              {/* <div className="invoice-placement-fields">
                <div className="invoice-placement-field">
                  <label>Candidate</label>
                  <div>{getInvoiceDetailValue("candidate_name")}</div>
                </div>
                <div className="invoice-placement-field">
                  <label>Job title</label>
                  <div>{getInvoiceDetailValue("job_title")}</div>
                </div>
                <div className="invoice-placement-field">
                  <label>Client / Company name</label>
                  <div>{getInvoiceDetailValue("client_name")}</div>
                </div>
                <div className="invoice-placement-field">
                  <label>Recruiter</label>
                  <div>
                    {formatRecruiterName(invoiceDetail || selectedInvoiceCandidate)}
                  </div>
                </div>
              </div> */}

              <div className="invoice-form-section">
                <h3>Invoice Details</h3>
                <p>Enter the details required to generate this invoice.</p>

                <div className="invoice-form-fields">
                  <div className="invoice-form-field">
                    <label htmlFor="joiningDate">
                      Joining Date
                      <span style={{ color: "red" }}> *</span>
                    </label>
                    <button
                      id="joiningDate"
                      type="button"
                      className="invoice-date-picker-trigger calendarschedulerIcons"
                      onClick={(event) =>
                        handleOpenInvoiceDatePicker("joiningDate", event)
                      }
                    >
                      {formatInvoiceDate(invoiceFormik.values.joiningDate)}
                    </button>
                    {renderInvoiceFieldError("joiningDate")}
                  </div>

                  <div className="invoice-form-field">
                    <label htmlFor="invoiceDate">
                      Invoice date
                      <span style={{ color: "red" }}> *</span>
                    </label>
                    <button
                      id="invoiceDate"
                      type="button"
                      className="invoice-date-picker-trigger calendarschedulerIcons"
                      onClick={(event) =>
                        handleOpenInvoiceDatePicker("invoiceDate", event)
                      }
                    >
                      {formatInvoiceDate(invoiceFormik.values.invoiceDate)}
                    </button>
                    {renderInvoiceFieldError("invoiceDate")}
                  </div>

                  {/* <div className="invoice-form-field">
                    <DynamicSelectInput
                      label="Country"
                      name="country"
                      options={countryOptions}
                      value={invoiceFormik.values.country}
                      onChange={invoiceFormik.handleChange}
                      defaultOption="Select"
                      menuWidth={220}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        display: "block",
                      }}
                      required
                    />
                    {renderInvoiceFieldError("country")}
                  </div> */}

                  <div className="invoice-form-field">
                    <DynamicSelectInput
                      label="Hiring Type"
                      name="hiringType"
                      options={hiringTypeOptions}
                      value={invoiceFormik.values.hiringType}
                      onChange={invoiceFormik.handleChange}
                      defaultOption="Select"
                      menuWidth={220}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        display: "block",
                      }}
                      required
                    />
                    {renderInvoiceFieldError("hiringType")}
                  </div>

                  {/* <div className="invoice-form-field">
                    <DynamicSelectInput
                      label="Fee Mode"
                      name="billingMode"
                      options={feeModeOptions}
                      value={invoiceFormik.values.billingMode}
                      onChange={invoiceFormik.handleChange}
                      defaultOption="Select"
                      menuWidth={220}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        display: "block",
                      }}
                      required
                    />
                  </div>

                  <div className="invoice-form-field">
                    <DynamicSelectInput
                      label="Contract Billing Mode"
                      name="contractBillingMode"
                      options={contractBillingModeOptions}
                      value={invoiceFormik.values.contractBillingMode}
                      onChange={invoiceFormik.handleChange}
                      defaultOption="Select"
                      menuWidth={220}
                      disabled={!isUSACanadaContract}
                      selectStyle={{
                        fontFamily: "Satoshi",
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "var(--header__color)",
                        borderRadius: "12px",
                        opacity: isUSACanadaContract ? 1 : 0.55,
                        cursor: isUSACanadaContract ? "pointer" : "not-allowed",
                      }}
                      labelStyle={{
                        color: "var(--sidepopup-label-color, #64748b)",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        display: "block",
                      }}
                    />
                  </div> */}
                </div>
              </div>

              {renderBillingDetailsScenario({
                showBillingModeToggle: true,
              })}
              {billingCalculation && (
                <InvoiceCalculationCard
                  billingCalculation={billingCalculation}
                  type="billing"
                  country={invoiceFormik.values.country}
                  billingMode={invoiceFormik.values.billingMode}
                />
              )}
              <div className="invoice-profitability-referral-card">
                <div className="invoice-profitability-card">
                  <div className="invoice-profitability-heading">
                    <div>
                      <h3>Margin & Profitability</h3>
                      <p>
                        Internal only. These values never appear on the
                        printable invoice.
                      </p>
                    </div>
                  </div>
                  <div className="invoice-profitability-grid">
                    <div className="invoice-profitability-field">
                      <DynamicTextField
                        label="Recruiter cost (prorated)"
                        name="recruiterCost"
                        value={invoiceFormik.values.recruiterCost}
                        onChange={handleMainMarginChange}
                        placeholder="0"
                        type="text"
                        containerStyle={{ width: "100%" }}
                        labelStyle={{
                          color: "var(--sidepopup-label-color, #64748b)",
                          fontSize: "14px",
                          fontWeight: 600,
                          marginBottom: "6px",
                        }}
                      />
                    </div>
                    <div className="invoice-profitability-field">
                      <DynamicTextField
                        label="Job board / Sourcing cost"
                        name="jobBoardSourcingCost"
                        value={invoiceFormik.values.jobBoardSourcingCost}
                        onChange={handleMainMarginChange}
                        placeholder="0"
                        type="text"
                        containerStyle={{ width: "100%" }}
                        labelStyle={{
                          color: "var(--sidepopup-label-color, #64748b)",
                          fontSize: "14px",
                          fontWeight: 600,
                          marginBottom: "6px",
                        }}
                      />
                    </div>
                    <div className="invoice-profitability-field">
                      <DynamicTextField
                        label="Background check cost"
                        name="backgroundCheckCost"
                        value={invoiceFormik.values.backgroundCheckCost}
                        onChange={handleMainMarginChange}
                        placeholder="0"
                        type="text"
                        containerStyle={{ width: "100%" }}
                        labelStyle={{
                          color: "var(--sidepopup-label-color, #64748b)",
                          fontSize: "14px",
                          fontWeight: 600,
                          marginBottom: "6px",
                        }}
                      />
                    </div>
                    <div className="invoice-profitability-field">
                      <DynamicTextField
                        label="Other operational costs"
                        name="otherOperationalCosts"
                        value={invoiceFormik.values.otherOperationalCosts}
                        onChange={handleMainMarginChange}
                        placeholder="0"
                        type="text"
                        containerStyle={{ width: "100%" }}
                        labelStyle={{
                          color: "var(--sidepopup-label-color, #64748b)",
                          fontSize: "14px",
                          fontWeight: 600,
                          marginBottom: "6px",
                        }}
                      />
                    </div>
                    {!invoiceFormik.values.referralIncluded && (
                      <>
                        <div className="invoice-referral-field">
                          <DynamicTextField
                            label={
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                Placement fee
                                <CustomTooltip
                                  title="Calculating the billing will obtain the placement fee."
                                  arrow
                                >
                                  <img
                                    alt="infoIcon"
                                    src={infoIcon}
                                    style={{ width: "18px" }}
                                  />
                                </CustomTooltip>
                              </span>
                            }
                            name="placementFee"
                            value={invoiceFormik.values.placementFee}
                            onChange={handleMainMarginChange}
                            placeholder="Enter placement fee"
                            type="number"
                            containerStyle={{ width: "100%" }}
                            labelStyle={{
                              color: "var(--sidepopup-label-color, #64748b)",
                              fontSize: "14px",
                              fontWeight: 600,
                              marginBottom: "6px",
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        </div>
                        <div className="invoice-profitability-field invoice-profitability-field--action">
                          {renderMainMarginCalculationButton()}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="invoice-referral-card">
                  <div className="invoice-referral-header">
                    <label className="invoice-referral-toggle">
                      <DynamicCheckbox
                        name="referralIncluded"
                        checked={invoiceFormik.values.referralIncluded}
                        onChange={(event) =>
                          setMainMarginFieldValue(
                            "referralIncluded",
                            event.target.checked,
                          )
                        }
                        isDarkMode={isDarkMode}
                        color="var(--primary__color)"
                      />
                      <span className="invoice-referral-title">
                        Placement includes referral
                      </span>
                    </label>

                    <p>
                      Attach a referral reward and deduct it live from net
                      profit.
                    </p>
                  </div>

                  {invoiceFormik.values.referralIncluded && (
                    <div className="invoice-referral-grid">
                      <div className="invoice-referral-field">
                        <DynamicSelectInput
                          label="Referrer type"
                          name="referrerType"
                          options={referrerTypeOptions}
                          value={invoiceFormik.values.referrerType}
                          onChange={handleMainMarginChange}
                          defaultOption="Select"
                          menuWidth={220}
                          labelStyle={{
                            color: "var(--sidepopup-label-color, #64748b)",
                            fontSize: "14px",
                            fontWeight: 600,
                            marginBottom: "6px",
                            display: "block",
                          }}
                        />
                      </div>

                      <div className="invoice-referral-field">
                        {invoiceFormik.values.referrerType ===
                        "INTERNAL_EMPLOYEE" ? (
                          <div>
                            <label
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "var(--sidepopup-label-color, #64748b)",
                              }}
                            >
                              Referrer
                            </label>
                            <DynamicListUsers
                              value={invoiceFormik.values.referrerUser}
                              onChange={(value) => {
                                setMainMarginFieldValue("referrerUser", value);
                              }}
                              placeholder="Search internal employee"
                            />
                          </div>
                        ) : (
                          <DynamicTextField
                            label="Referrer"
                            name="referrer"
                            value={invoiceFormik.values.referrer}
                            onChange={handleMainMarginChange}
                            placeholder="Enter referrer name"
                            type="text"
                            containerStyle={{ width: "100%" }}
                            labelStyle={{
                              color: "var(--sidepopup-label-color, #64748b)",
                              fontSize: "14px",
                              fontWeight: 600,
                              marginBottom: "6px",
                            }}
                          />
                        )}
                      </div>
                      <div className="invoice-referral-field">
                        <DynamicTextField
                          label={
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              Placement fee
                              <CustomTooltip
                                title="Calculating the billing will obtain the placement fee."
                                arrow
                              >
                                <img
                                  alt="infoIcon"
                                  src={infoIcon}
                                  style={{ width: "18px" }}
                                />
                              </CustomTooltip>
                            </span>
                          }
                          name="placementFee"
                          value={invoiceFormik.values.placementFee}
                          onChange={handleMainMarginChange}
                          placeholder="Enter placement fee"
                          type="number"
                          containerStyle={{ width: "100%" }}
                          labelStyle={{
                            color: "var(--sidepopup-label-color, #64748b)",
                            fontSize: "14px",
                            fontWeight: 600,
                            marginBottom: "6px",
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </div>
                      <div className="invoice-referral-field">
                        <DynamicSelectInput
                          label="Reward type"
                          name="rewardType"
                          options={rewardTypeOptions}
                          value={invoiceFormik.values.rewardType}
                          onChange={handleMainMarginChange}
                          defaultOption="Select"
                          menuWidth={220}
                          labelStyle={{
                            color: "var(--sidepopup-label-color, #64748b)",
                            fontSize: "14px",
                            fontWeight: 600,
                            marginBottom: "6px",
                            display: "block",
                          }}
                        />
                      </div>
                      <div className="invoice-referral-field">
                        <DynamicTextField
                          label={`Reward value (${
                            invoiceFormik.values.rewardType === "PERCENTAGE"
                              ? "%"
                              : ["USA", "UNITED STATES", "CANADA"].includes(
                                    normalizeInvoiceCountry(
                                      invoiceFormik.values.country,
                                    ),
                                  )
                                ? "$"
                                : "₹"
                          })`}
                          name="rewardPercent"
                          value={invoiceFormik.values.rewardPercent}
                          onChange={handleMainMarginChange}
                          placeholder="Enter reward value"
                          type="number"
                          containerStyle={{ width: "100%" }}
                          labelStyle={{
                            color: "var(--sidepopup-label-color, #64748b)",
                            fontSize: "14px",
                            fontWeight: 600,
                            marginBottom: "6px",
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </div>
                      <div className="invoice-referral-field invoice-referral-field--action">
                        {renderMainMarginCalculationButton()}
                      </div>
                    </div>
                  )}
                </div>
                {marginCalculation && (
                  <InvoiceCalculationCard
                    marginCalculation={marginCalculation}
                    type="margin"
                  />
                )}
              </div>
            </form>

            {activeInvoiceDatePicker &&
              createPortal(
                <CustomDatePickerModal
                  initialDate={getDateValue(
                    activeInvoiceDatePickerSource === "popup"
                      ? popupBillingValues[activeInvoiceDatePicker]
                      : invoiceFormik.values[activeInvoiceDatePicker],
                  )}
                  disablePast={false}
                  onOk={(date) => {
                    if (activeInvoiceDatePickerSource === "popup") {
                      setPopupBillingCalculation(null);
                      setPopupBillingValues((previousValues) => ({
                        ...previousValues,
                        [activeInvoiceDatePicker]: date,
                      }));
                    } else {
                      invoiceFormik.setFieldValue(
                        activeInvoiceDatePicker,
                        date,
                      );
                    }
                    handleCloseInvoiceDatePicker();
                  }}
                  onClose={handleCloseInvoiceDatePicker}
                  top={invoiceDatePickerPosition.top}
                  left={invoiceDatePickerPosition.left}
                  zIndex={2000}
                />,
                document.body,
              )}
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button
                label={
                  isInvoicePreviewLoading ? "Generating..." : "Invoice Preview"
                }
                backgroundColor="transparent"
                color="#6860ee"
                border="1px solid #6860ee"
                hoverStyles={{
                  backgroundColor: "#6860ee",
                  color: "#ffffff",
                  transform: "none",
                }}
                disabled={!isInvoicePreviewReady || isInvoicePreviewLoading}
                event={handlePreviewBeforeCreate}
              />
              {isEditing && (
                <Button
                  label="Publish Invoice"
                  backgroundColor="transparent"
                  color="#16a34a"
                  border="1px solid #16a34a"
                  hoverStyles={{
                    backgroundColor: "#16a34a",
                    color: "#ffffff",
                    transform: "none",
                  }}
                  event={() => setIsPublishConfirmOpen(true)}
                />
              )}
              <Button
                label={isEditing ? "Update" : "Create"}
                event={createInvoice}
              />
            </div>
          </div>
        )}
      </SidePopup>
      <SidePopup
        isOpen={isBillingCalculationPopupOpen}
        onClose={() => setIsBillingCalculationPopupOpen(false)}
        title="Billing Calculation"
        sidePopupWidth="min(760px, 92%)"
        contentPadding="18px"
      >
        <div className="invoice-billing-calculation-popup">
          {renderBillingDetailsScenario({
            values: popupBillingValues,
            handleChange: handlePopupBillingChange,
            taxConfigs: popupSelectedTaxConfigs,
            setTaxConfigs: handlePopupTaxConfigsChange,
            calculateEvent: handleCalculateBillingInPopup,
            calculationLoading: isBillingCalculationLoading,
            showBillingModeToggle: true,
            datePickerSource: "popup",
            sectionClassName:
              "invoice-billing-section invoice-billing-section--popup",
          })}
          {popupBillingCalculation && (
            <InvoiceCalculationCard
              billingCalculation={popupBillingCalculation}
              type="billing"
              country={popupBillingValues.country}
              billingMode={popupBillingValues.billingMode}
            />
          )}
        </div>
      </SidePopup>
      <SidePopup
        isOpen={isMarginCalculationPopupOpen}
        onClose={() => setIsMarginCalculationPopupOpen(false)}
        title="Margin Calculation"
        sidePopupWidth="min(760px, 92%)"
        contentPadding="18px"
      >
        {renderMarginProfitabilityScenario({
          values: popupMarginValues,
          handleChange: handlePopupMarginChange,
          setFieldValue: setPopupMarginFieldValue,
          calculation: popupMarginCalculation,
          calculateEvent: handleCalculateMarginInPopup,
          calculationLoading: isMarginCalculationLoading,
        })}
      </SidePopup>
      <StandardPopup
        isOpen={isInvoicePreviewOpen}
        onClose={() => {
          setIsInvoicePreviewOpen(false);
          setInvoicePreviewHtml("");
        }}
        title="Invoice Preview"
        width="60%"
        maxHeight="90vh"
        hideActions
        contentStyle={{
          padding: "0",
          overflow: "hidden",
        }}
      >
        <EmailPreviewPopup htmlContent={invoicePreviewHtml} />
      </StandardPopup>
      {isPublishConfirmOpen &&
        createPortal(
          <ConfirmationPopup
            isOpen={isPublishConfirmOpen}
            onClose={() => setIsPublishConfirmOpen(false)}
            title="Publish Invoice"
            subTitle="Mark invoice as Outstanding?"
            message="This will move the invoice to Outstanding status and lock it for editing.
Once marked as Outstanding, the invoice details cannot be changed."
            confirmLabel={
              isPublishingInvoice ? "Marking..." : "Yes,Mark as Outstanding"
            }
            onConfirm={() => changeInvoiceStatus("outstanding")}
          />,
          document.body,
        )}
      {isPaidConfirmOpen &&
        createPortal(
          <ConfirmationPopup
            isOpen={isPaidConfirmOpen}
            onClose={() => setIsPaidConfirmOpen(false)}
            title="Mark as Paid"
            subTitle="Mark invoice as Paid?"
            message="This will move the invoice to paid status and lock it for editing. Once marked as paid, the invoice details cannot be changed."
            confirmLabel={
              isPublishingInvoice ? "Marking..." : "Yes, Mark as Paid"
            }
            onConfirm={() => changeInvoiceStatus("paid")}
          />,
          document.body,
        )}
      {isCancelConfirmOpen &&
        createPortal(
          <ConfirmationPopup
            isOpen={isCancelConfirmOpen}
            onClose={() => setIsCancelConfirmOpen(false)}
            title="Cancel Invoice"
            subTitle="Cancel this invoice?"
            message="This will move the invoice to canceled status and it will be deleted, you can't download this invoice anymore, it will be history. You can preview the invoice, and the canceled invoice's candidate will be moved to Ready to invoice."
            confirmLabel={
              isPublishingInvoice ? "Canceling..." : "Yes, Cancel Invoice"
            }
            onConfirm={() => changeInvoiceStatus("cancelled")}
          />,
          document.body,
        )}
    </div>
  );
}

export default InvoiceList;